class WhatsInBox {
  constructor(container) {
    this.container = container;
    // Select both regular product cards and add-on cards
    this.productCards = this.container.querySelectorAll('.fsb-whats-in-box__product-card, .fsb-add-ons__product-card');
    this.errorMessage = this.container.querySelector('.fsb-whats-in-box__error-message');

    // Parse bundle configuration
    this.bundleConfig = JSON.parse(this.container.dataset.bundleConfig || '{}');
    this.bundleVariantId = parseInt(this.container.dataset.bundleVariantId, 10);
    this.bundleProductId = this.container.dataset.productId;
    this.colorSwatchOptions = JSON.parse(this.container.dataset.colorSwatchOptions || '[]');
    this.colorConfig = this.parseColorConfig(this.container.dataset.colorConfig || '');
    this.blockSettings = JSON.parse(this.container.dataset.blockSettings || '{}');

    // Track selected variants and options
    this.selectedVariants = new Map();
    this.selectedOptions = new Map();
    
    // Track selected add-ons (using Set to track variant IDs)
    this.selectedAddOns = new Set();
    
    // Add-on limits from settings
    this.addOnLimits = {
      minimum: this.bundleConfig?.settings?.add_on?.minimum || 0,
      maximum: this.bundleConfig?.settings?.add_on?.maximum || 0
    };

    // Form Elements
    this.productForm = document.querySelector('.shopify-product-form');
    this.variantIdInput = this.productForm ? this.productForm.querySelector('input[name="id"]') : null;
    this.addToCartButton = this.productForm ? this.productForm.querySelector('button[type="submit"]') : null;
    
    // Start Building Button
    this.startBuildingButton = this.container.querySelector('.fsb-whats-in-box__start-building-button');
    
    // Price display element - try multiple selectors to find it
    this.priceListElement = document.querySelector('[data-product-price-list]');
    if (!this.priceListElement) {
      // Try alternative selector
      this.priceListElement = document.querySelector('.price-list[data-product-price-list]');
    }

    // Store original price state for reset functionality
    if (this.priceListElement) {
      const priceContainer = this.priceListElement.closest('.product-meta__price-list-container');
      
      // Store original current price
      const currentPriceEl = this.priceListElement.querySelector('[data-price-current]');
      this.originalPrice = currentPriceEl ? currentPriceEl.textContent.trim() : null;
      
      // Store original compare price state
      const comparePriceWrapper = this.priceListElement.querySelector('.price--compare');
      this.originalComparePriceVisible = comparePriceWrapper ? (comparePriceWrapper.style.display !== 'none') : false;
      const comparePriceEl = this.priceListElement.querySelector('[data-price-compare]');
      this.originalComparePrice = comparePriceEl ? comparePriceEl.textContent.trim() : null;
      
      // Store original savings badge state
      const savingsEl = priceContainer ? priceContainer.querySelector('[data-price-savings]') : null;
      this.originalSavingsVisible = savingsEl ? (savingsEl.style.display !== 'none') : false;
      this.originalSavingsText = savingsEl ? savingsEl.textContent.trim() : null;
    }

    // Dynamic pricing elements
    this.currencyFormat = this.container.dataset.currencyFormat;
    this.priceSelector = this.blockSettings.price_selector;
    this.compareAtPriceSelector = this.blockSettings.compare_at_price_selector;
    this.priceElements = this.priceSelector ? document.querySelectorAll(this.priceSelector) : [];
    this.compareAtPriceElements = this.compareAtPriceSelector ? document.querySelectorAll(this.compareAtPriceSelector) : [];
    this.isDynamicPricing = this.bundleConfig?.settings?.price?.strategy === 'dynamic_pricing';
    this.discountPercentage = this.isDynamicPricing ? parseFloat(this.bundleConfig?.settings?.price?.value || 0) : 0;

    // Modal state - modals are pre-rendered per product
    this.currentModal = null;
    this.currentModalCard = null;
    this.modalSelectedOptions = {};
    this.modalSwipers = new Map(); // Track initialized swipers per modal
    
    // No longer using templates - price elements use data attributes directly

    this.init();
  }

  parseColorConfig(configStr) {
    const colorMap = new Map();
    if (!configStr) return colorMap;

    configStr.split('\n').forEach(line => {
      if (!line.trim()) return;
      const [name, code] = line.split(':').map(s => s.trim());
      if (name && code) {
        colorMap.set(name.toLowerCase(), code);
      }
    });

    return colorMap;
  }

  init() {
    this.setupInitialVariants();
    this.setupColorSwatches();
    this.setupAccordions();
    this.setupAddOnButtons();
    this.setupStartBuildingButton();
    this.setupModal();
    this.bindEvents();

    // Required-in-bundle: compute combined price/compare (discount + FoxSell logic) and update card display
    this.productCards.forEach(card => {
      if (card.dataset.requiredInBundle === 'true' && card.dataset.categoryTitle !== 'add-ons') {
        this.updateRequiredInBundleProductPrice(card);
      }
    });

    // Initialize pricing
    this.updateTotalBundlePrice();
    
    // Initialize pricing if dynamic pricing is enabled
    if (this.isDynamicPricing) {
      this.updateBundlePricing();
    }
    
    // Update button state after DOM is fully loaded
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Double-check with a small delay to ensure form is ready
      setTimeout(() => {
        this.updateAddToCartButtonState();
      }, 0);
    });
  }

  setupAccordions() {
    // Set up accordion functionality for regular product cards (not add-ons)
    const regularProductCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle !== 'add-ons'
    );
    
    // Store reference to cards
    this.regularProductCards = regularProductCards;
    
    // Set up checkmark click handlers to deselect products
    this.setupCheckmarkHandlers();
  }
  
  setupCheckmarkHandlers() {
    this.regularProductCards.forEach(card => {
      if (card.dataset.requiredInBundle === 'true') return;
      const checkmark = card.querySelector('.fsb-whats-in-box__checkmark');
      if (checkmark) {
        checkmark.addEventListener('click', (e) => {
          e.stopPropagation();
          this.deselectProduct(card);
        });
      }
    });
  }
  
  deselectProduct(card) {
    if (card.dataset.requiredInBundle === 'true') return;
    const categoryTitle = card.dataset.categoryTitle;
    const productId = card.dataset.productId;
    const variantKey = `${categoryTitle}-${productId}`;
    
    // Check if this product is currently selected
    if (!this.selectedVariants.has(variantKey)) {
      return; // Nothing to deselect
    }
    
    // Remove from selected variants and options
    this.selectedVariants.delete(variantKey);
    this.selectedOptions.delete(variantKey);
    
    // Remove selected class from card
    card.classList.remove('fsb-whats-in-box__product-card--selected');
    
    // Reset the "Change Option" button back to "See Options"
    const selectOptionBtn = card.querySelector('[data-see-details-trigger]');
    if (selectOptionBtn) {
      selectOptionBtn.textContent = 'See Options';
      selectOptionBtn.dataset.hasSelection = 'false';
    }
    
    // Reset the selection option values to default
    const selectionOptions = card.querySelectorAll('.fsb-whats-in-box__selection-option');
    selectionOptions.forEach(selectionOption => {
      const optionName = selectionOption.dataset.optionName;
      const valueSpan = selectionOption.querySelector('.fsb-whats-in-box__selection-option-value');
      if (valueSpan) {
        valueSpan.textContent = `Select ${optionName}`;
        valueSpan.classList.remove('fsb-whats-in-box__selection-option-value--selected');
      }
    });
    
    // Reset product image to default
    const imageContainer = card.querySelector('.fsb-whats-in-box__product-image-container');
    if (imageContainer) {
      const allImages = imageContainer.querySelectorAll('.fsb-whats-in-box__variant-image');
      allImages.forEach(img => {
        img.classList.remove('fsb-whats-in-box__variant-image--active');
      });
      const defaultImage = imageContainer.querySelector('[data-variant-image="default"]');
      if (defaultImage) {
        defaultImage.classList.add('fsb-whats-in-box__variant-image--active');
      }
    }
    
    // Update line item properties
    this.updateLineItemProperties();
    
    // Update price displays
    this.updateTotalBundlePrice();
    this.updateAddToCartButtonWithPrice();
    
    // Update button state
    this.updateAddToCartButtonState();
  }

  setupStartBuildingButton() {
    const startButton = this.container.querySelector('.fsb-whats-in-box__start-building-button');
    if (!startButton) return;
    
    startButton.addEventListener('click', () => {
      // Click the first product's "Select Option" button to open its modal
      if (this.regularProductCards && this.regularProductCards.length > 0) {
        const firstCard = this.regularProductCards[0];
        const selectBtn = firstCard.querySelector('[data-see-details-trigger]');
        if (selectBtn) {
          selectBtn.click();
        }
      }
      
      // Hide the button with class for smooth transition
      startButton.classList.add('fsb-whats-in-box__start-building-button--hidden');
    });
  }

  setupColorSwatches() {
    this.productCards.forEach(card => {
      const optionElements = card.querySelectorAll('.fsb-whats-in-box__option');
      optionElements.forEach(optionEl => {
        const optionName = optionEl.dataset.optionName;

        // Check if this option should be a color swatch
        const isColorOption = this.colorSwatchOptions.some(opt =>
          opt.toLowerCase() === optionName.toLowerCase()
        );

        if (isColorOption) {
          const buttons = optionEl.querySelectorAll('.fsb-whats-in-box__color-swatch');
          buttons.forEach(button => {
            const value = button.dataset.value;
            if (value) {
              const colorCode = this.colorConfig.get(value.toLowerCase());
              if (colorCode) {
                button.style.setProperty('--swatch-background', colorCode);
              }
            }
          });
        }
      });
    });
  }

  setupInitialVariants() {
    this.productCards.forEach(card => {
      try {
        const categoryTitle = card.dataset.categoryTitle;
        const isAddOn = categoryTitle === 'add-ons';
        const variantKey = `${categoryTitle}-${card.dataset.productId}`;

        // Required-in-bundle products are always included in the bundle and their combined
        // price is handled separately (see updateRequiredInBundleProductPrice). For these,
        // we intentionally skip variant initialization/tracking here.
        if (card.dataset.requiredInBundle === 'true') return;

        const variants = JSON.parse(card.dataset.variants || '[]');
        const appVariants = JSON.parse(card.dataset.appVariants || '{}');

        // Check if this is a split product (no option elements, single variant) - check both class names
        const hasOptionElements = card.querySelector('.fsb-whats-in-box__option, .fsb-add-ons__option') !== null;
        
        // Use product-specific key to track variants per product card
        // This allows multiple products in the same category to each have selections
        
        // Update option availability (disable unavailable options)
        if (!isAddOn) {
          const firstOptionEl = card.querySelector('.fsb-whats-in-box__option[data-option-index="0"]') ||
                                card.querySelector('.fsb-add-ons__option[data-option-index="0"]');
          if (firstOptionEl) {
            const optionValues = firstOptionEl.querySelectorAll('[data-value]');
            optionValues.forEach(valueEl => {
              const value = valueEl.dataset.value;
              // Check if any variant exists with this first option value
              const hasAvailableVariant = variants.some(v =>
                v.available && v.id in appVariants && v.options[0] === value
              );

              valueEl.disabled = !hasAvailableVariant;
              if (!hasAvailableVariant) {
                valueEl.classList.add('disabled');
              }
            });
          }
        }

        // Initialize with empty selection (use array of empty strings for option availability checking)
        this.selectedVariants.set(variantKey, null);
        this.selectedOptions.set(variantKey, Array(3).fill(''));
        
        // If show_price is enabled, show price for first available variant even if no options selected
        if (!isAddOn && this.blockSettings.show_price) {
          const firstAvailableVariant = variants.find(v => v.available && v.id in appVariants);
          if (firstAvailableVariant) {
            this.updateProductPrice(card, firstAvailableVariant, appVariants);
          }
        } else if (!isAddOn && this.isDynamicPricing) {
          // If dynamic pricing is enabled but show_price is not, still show price when variant is selected
          const firstAvailableVariant = variants.find(v => v.available && v.id in appVariants);
          if (firstAvailableVariant) {
            this.updateProductPrice(card, firstAvailableVariant, appVariants);
          }
        }
        
        // Update option availability for all options (not just first)
        if (!isAddOn && hasOptionElements) {
          this.updateOptionAvailability(card, null);
          
          // Auto-select options that have only one available value (using Liquid's single-option class)
          const optionElements = card.querySelectorAll('.fsb-whats-in-box__option, .fsb-add-ons__option');
          optionElements.forEach((optionEl, optionIndex) => {
            // Check if this option has the single-option class (only one available value) - check both class names
            const isSingleOption = optionEl.classList.contains('fsb-whats-in-box__option-value--single') ||
                                  optionEl.classList.contains('fsb-add-ons__option-value--single');
            if (isSingleOption) {
              // Find the single available option value
              const availableOptions = optionEl.querySelectorAll('[data-value]:not(.disabled):not([disabled])');
              if (availableOptions.length === 1) {
                // Auto-select this single option
                this.selectOptionValue(availableOptions[0], false);
              }
            }
          });
          
          // Update all selection option values after initial setup
          const variantKey = `${categoryTitle}-${card.dataset.productId}`;
          const currentOptions = this.selectedOptions.get(variantKey) || Array(3).fill('');
          currentOptions.forEach((value, index) => {
            if (value && value !== '') {
              this.updateSelectionOptionValue(card, index, value);
            }
          });
        }
      } catch (error) {
        // Silently handle errors
      }
    });
  }

  updateOptionAvailability(card, selectedIndex) {
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    const categoryTitle = card.dataset.categoryTitle;
    const isAddOn = categoryTitle === 'add-ons';
    const variantKey = `${categoryTitle}-${card.dataset.productId}`;
    const currentOptions = this.selectedOptions.get(variantKey) || Array(3).fill('');
    // Check both regular and add-on option classes
    const optionElements = card.querySelectorAll('.fsb-whats-in-box__option, .fsb-add-ons__option');

    // For each option in the product
    optionElements.forEach((optionEl, optionIndex) => {
      if (optionIndex !== selectedIndex) { // Skip the currently selected option
        const optionValues = optionEl.querySelectorAll('[data-value]');

        optionValues.forEach(valueEl => {
          const value = valueEl.dataset.value;

          // Check if any variant exists with these options
          // If currentOptions[i] is empty, don't filter by that option (allow any value)
          const hasAvailableVariant = variants.some(variant => {
            if (!variant.available || !(variant.id in appVariants)) {
              return false;
            }
            
            // Check if this variant matches the test options
            return variant.options.every((opt, i) => {
              if (i === optionIndex) {
                // This is the option we're testing - must match the test value
                return opt === value;
              } else {
                // For other options, if currentOptions[i] is empty, allow any value
                // Otherwise, must match the current selection
                return currentOptions[i] === '' || opt === currentOptions[i];
              }
            });
          });

          // Disable/enable the option based on availability
          valueEl.disabled = !hasAvailableVariant;
          if (!hasAvailableVariant) {
            valueEl.classList.add('disabled');
            const selectedClass = 'fsb-whats-in-box__option-value--selected';
            valueEl.classList.remove(selectedClass);
          } else {
            valueEl.classList.remove('disabled');
          }
        });
      }
    });
  }

  selectOptionValue(element, updateAvailability = true) {
    // Find card - check both regular and add-on class names
    const card = element.closest('.fsb-whats-in-box__product-card') || 
                 element.closest('.fsb-add-ons__product-card');
    // Find option - check both regular and add-on class names
    const optionEl = element.closest('.fsb-whats-in-box__option') || 
                     element.closest('.fsb-add-ons__option');
    const optionIndex = parseInt(optionEl?.dataset.optionIndex, 10);
    const value = element.dataset.value;
    const categoryTitle = card.dataset.categoryTitle;
    const isAddOn = categoryTitle === 'add-ons';
    
    // Use product-specific key
    const variantKey = `${categoryTitle}-${card.dataset.productId}`;

    // Get current options
    const currentOptions = this.selectedOptions.get(variantKey) || Array(3).fill('');
    
    // Check if this option is already selected (toggle behavior) - check both class names
    const isColorSwatch = element.classList.contains('fsb-whats-in-box__color-swatch') || 
                          element.classList.contains('fsb-add-ons__color-swatch');
    const isAddOnCard = categoryTitle === 'add-ons';
    const prefix = isAddOnCard ? 'fsb-add-ons' : 'fsb-whats-in-box';
    const selectedClass = isColorSwatch ? `${prefix}__color-swatch--selected` : `${prefix}__option-value--selected`;
    const isAlreadySelected = element.classList.contains(selectedClass) && currentOptions[optionIndex] === value;
    
    if (isAlreadySelected) {
      // Deselect: remove selected class and clear the option value
      element.classList.remove(selectedClass);
      currentOptions[optionIndex] = '';
      this.selectedOptions.set(variantKey, currentOptions);
      
      // Update selection option value display (clear it)
      this.updateSelectionOptionValue(card, optionIndex, '');
      
      // Update variant availability
      if (updateAvailability) {
        this.updateOptionAvailability(card, optionIndex);
      }
      
      // Find if there's still a valid variant with the remaining options
      const variants = JSON.parse(card.dataset.variants || '[]');
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      const selectedVariant = variants.find(v =>
        v.available && v.id in appVariants && v.options.every((opt, i) => opt === currentOptions[i])
      );
      
      if (selectedVariant) {
        this.selectedVariants.set(variantKey, selectedVariant);
        if (this.isDynamicPricing) {
          this.updateProductPrice(card, selectedVariant, appVariants);
        }
      } else {
        // No valid variant with current selections
        this.selectedVariants.set(variantKey, null);
        this.resetProductImage(card);
        
        // If show_price is enabled, show price for first available variant even when no options selected
        if (!isAddOn && this.blockSettings.show_price) {
          const variants = JSON.parse(card.dataset.variants || '[]');
          const appVariants = JSON.parse(card.dataset.appVariants || '{}');
          const firstAvailableVariant = variants.find(v => v.available && v.id in appVariants);
          if (firstAvailableVariant) {
            this.updateProductPrice(card, firstAvailableVariant, appVariants);
          } else {
            this.clearProductPrice(card);
          }
        } else if (this.isDynamicPricing) {
          this.clearProductPrice(card);
        }
      }
      
      // Update card selected state
      if (!isAddOn) {
        card.classList.remove('fsb-whats-in-box__product-card--selected');
      }
      
      // Update bundle pricing and button state
      if (this.isDynamicPricing) {
        this.updateBundlePricing();
      }
      this.updateTotalBundlePrice();
      this.updateAddToCartButtonState();
      this.updateLineItemProperties();
      
      return;
    }

    // Normal selection: update selected options
    currentOptions[optionIndex] = value;
    this.selectedOptions.set(variantKey, currentOptions);

    // Update selection option value display
    this.updateSelectionOptionValue(card, optionIndex, value);

    // Update UI
    // Remove selected class from siblings - check both regular and add-on class names
    const optionValues = element.closest('.fsb-whats-in-box__option-values') || 
                         element.closest('.fsb-add-ons__option-values');
    if (optionValues) {
      optionValues.querySelectorAll(
        '.fsb-whats-in-box__color-swatch--selected, .fsb-whats-in-box__option-value--selected, ' +
        '.fsb-add-ons__color-swatch--selected, .fsb-add-ons__option-value--selected'
      ).forEach(el => el.classList.remove(selectedClass));
    }

    // Add selected class to this option
    element.classList.add(selectedClass);

    // Update variant availability
    if (updateAvailability) {
      this.updateOptionAvailability(card, optionIndex);
    }

    // Find and update selected variant
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    const selectedVariant = variants.find(v =>
      v.available && v.id in appVariants && v.options.every((opt, i) => opt === currentOptions[i])
    );

    if (selectedVariant) {
      this.selectedVariants.set(variantKey, selectedVariant);
      // Update product image if variant has an image
      this.updateProductImage(card, selectedVariant);

      // Update gallery image for main bundle products (not add-ons)
      if (!isAddOn) {
        this.updateGalleryImage(selectedVariant);
      }

      // Update product price display (always if show_price is enabled, or if dynamic pricing is enabled)
      if ((!isAddOn && this.blockSettings.show_price) || this.isDynamicPricing) {
        this.updateProductPrice(card, selectedVariant, appVariants);
      }
      
      // Check if all options are selected
      const optionElements = isAddOn 
        ? card.querySelectorAll('.fsb-add-ons__option')
        : card.querySelectorAll('.fsb-whats-in-box__option');
      const totalOptions = optionElements.length;
      const selectedOptionsCount = currentOptions.filter(opt => opt !== '').length;
      
      // If all options are selected, add the selected class
      if (selectedOptionsCount === totalOptions && totalOptions > 0) {
        const selectedClass = isAddOn 
          ? 'fsb-add-ons__product-card--selected'
          : 'fsb-whats-in-box__product-card--selected';
        card.classList.add(selectedClass);
        
      } else {
        card.classList.remove('fsb-whats-in-box__product-card--selected');
        card.classList.remove('fsb-add-ons__product-card--selected');
      }
      
        // For add-ons, if this variant is already selected, keep it selected
        if (isAddOn && this.selectedAddOns.has(selectedVariant.id)) {
          card.classList.add('fsb-add-ons__product-card--selected');
          // Update button state
          this.updateAddOnButton(card, selectedVariant, appVariants, true);
        } else if (isAddOn) {
          // Update button with selected variant
          this.updateAddOnButton(card, selectedVariant, appVariants, false);
        }
    } else {
      this.selectedVariants.set(variantKey, null);
      // Reset to default product image
      this.resetProductImage(card);

      // If show_price is enabled, show price for first available variant even when no options selected
      if (!isAddOn && this.blockSettings.show_price) {
        const variants = JSON.parse(card.dataset.variants || '[]');
        const appVariants = JSON.parse(card.dataset.appVariants || '{}');
        const firstAvailableVariant = variants.find(v => v.available && v.id in appVariants);
        if (firstAvailableVariant) {
          this.updateProductPrice(card, firstAvailableVariant, appVariants);
        } else {
          this.clearProductPrice(card);
        }
      } else if (this.isDynamicPricing) {
        // Clear product price display if dynamic pricing is enabled but show_price is not
        this.clearProductPrice(card);
      }
      
      // Remove selected class if variant is no longer available
      if (!isAddOn) {
        card.classList.remove('fsb-whats-in-box__product-card--selected');
      }
      
      // For add-ons, remove selection if variant is no longer available
      if (isAddOn) {
        const currentVariant = this.selectedVariants.get(variantKey);
        if (currentVariant) {
          this.selectedAddOns.delete(currentVariant.id);
          card.classList.remove('fsb-add-ons__product-card--selected');
        }
      }
    }

    // Update bundle pricing if dynamic pricing is enabled
    if (this.isDynamicPricing) {
      this.updateBundlePricing();
    }
    
    // Update total bundle price display
    this.updateTotalBundlePrice();

    // Update button state after any selection change (product or add-on)
    this.updateAddToCartButtonState();
    
    this.updateLineItemProperties();

    document.dispatchEvent(new CustomEvent('foxsell:mix-and-match-pdp:option-selected', {
      detail: {
        variant: this.selectedVariants
      }
    }));
  }

  updateProductImage(card, variant) {
    // Get all variant images in the card
    const imageContainer = card.querySelector('.fsb-whats-in-box__product-image-container') ||
                          card.querySelector('.fsb-add-ons__product-image-container');
    if (!imageContainer) return;

    const allImages = imageContainer.querySelectorAll('.fsb-whats-in-box__variant-image, .fsb-add-ons__variant-image');
    if (allImages.length === 0) return;

    // Hide all images first
    allImages.forEach(img => {
      img.classList.remove('fsb-whats-in-box__variant-image--active');
      img.classList.remove('fsb-add-ons__variant-image--active');
    });

    // Find and show the variant's image
    const variantId = variant.id;
    let targetImage = imageContainer.querySelector(`[data-variant-image="${variantId}"]`);
    
    // If no variant-specific image found, show the default image
    if (!targetImage) {
      targetImage = imageContainer.querySelector('[data-variant-image="default"]');
    }

    if (targetImage) {
      targetImage.classList.add('fsb-whats-in-box__variant-image--active');
    }
  }

  resetProductImage(card) {
    // Get all variant images in the card
    const imageContainer = card.querySelector('.fsb-whats-in-box__product-image-container') ||
                          card.querySelector('.fsb-add-ons__product-image-container');
    if (!imageContainer) return;

    const allImages = imageContainer.querySelectorAll('.fsb-whats-in-box__variant-image, .fsb-add-ons__variant-image');
    
    // Hide all images first
    allImages.forEach(img => {
      img.classList.remove('fsb-whats-in-box__variant-image--active');
      img.classList.remove('fsb-add-ons__variant-image--active');
    });

    // Show the default image
    const defaultImage = imageContainer.querySelector('[data-variant-image="default"]');
    if (defaultImage) {
      defaultImage.classList.add('fsb-whats-in-box__variant-image--active');
    }
  }

  updateGalleryImage(variant) {
    if (!variant || !variant.id) return;

    // Find the product media carousel
    const mediaCarousel = document.querySelector('flickity-carousel.product__media-list');
    if (!mediaCarousel) return;

    // Find gallery item by variant ID (bundle variant images have data-variant-id)
    const galleryItem = mediaCarousel.querySelector(`[data-variant-id="${variant.id}"]`);
    if (!galleryItem) return;

    // Select the gallery item using the carousel's select method
    const mediaId = galleryItem.getAttribute('data-media-id');
    if (mediaId && mediaCarousel.select) {
      mediaCarousel.select(`[data-media-id="${mediaId}"]`);
    }
  }

  updateSelectionOptionValue(card, optionIndex, selectedValue) {
    // Only update for regular products (not add-ons)
    const isAddOn = card.dataset.categoryTitle === 'add-ons';
    if (isAddOn) return;

    // Find the corresponding selection option display by index
    const selectionOption = card.querySelector(`.fsb-whats-in-box__selection-option[data-option-index="${optionIndex}"]`);
    if (!selectionOption) {
      return;
    }

    // Find the value span
    const valueSpan = selectionOption.querySelector('.fsb-whats-in-box__selection-option-value');
    if (!valueSpan) {
      return;
    }

    // Get the option name from the selection option element or label
    const optionName = selectionOption.dataset.optionName;
    const optionLabel = selectionOption.querySelector('.fsb-whats-in-box__option-label');
    const displayOptionName = optionLabel ? optionLabel.textContent.trim() : (optionName || 'Option');

    // Update the value span with selected value or default text
    if (selectedValue && selectedValue !== '') {
      valueSpan.textContent = selectedValue;
      valueSpan.classList.add('fsb-whats-in-box__selection-option-value--selected');
    } else {
      valueSpan.textContent = `Select ${displayOptionName}`;
      valueSpan.classList.remove('fsb-whats-in-box__selection-option-value--selected');
    }
  }

  updateLineItemProperties() {
    // Get regular bundle products (excluding add-ons)
    const regularProductCards = Array.from(this.productCards)
      .filter(card => card.dataset.categoryTitle !== 'add-ons');
    
    // Build selection using FoxSell-compatible format
    // IMPORTANT: category field is REQUIRED by FoxSell for proper bundle processing
    const selection = [];
    /** Human-readable lines for order admin only (key prefixed with _ so theme hides in cart) */
    const adminDisplayLines = [];
    const getProductTitle = (card) => {
      const el = card.querySelector('.fsb-whats-in-box__product-title') || card.querySelector('.fsb-add-ons__product-title');
      return el ? el.textContent.trim() : '';
    };

    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const productId = card.dataset.productId;
      const variantKey = `${categoryTitle}-${productId}`;
      const productTitle = getProductTitle(card) || categoryTitle;

      if (card.dataset.requiredInBundle === 'true') {
        const variants = JSON.parse(card.dataset.variants || '[]');
        variants.forEach(variant => {
          selection.push({
            variantId: parseInt(variant.id, 10),
            quantity: 1,
            category: categoryTitle,
            type: "product",
            properties: {}
          });
          const title = variants.length === 1 ? productTitle : productTitle + ' - ' + (variant.title || '');
          adminDisplayLines.push({ title, quantity: 1, sku: variant.sku != null ? variant.sku : '' });
        });
        return; // Done with this card; continue to next
      }

      const variant = this.selectedVariants.get(variantKey);
      if (!variant) return;

      selection.push({
        variantId: parseInt(variant.id, 10),
        quantity: 1,
        category: categoryTitle,
        type: "product",
        properties: {}
      });
      const variants = JSON.parse(card.dataset.variants || '[]');
      const title = variants.length === 1 ? productTitle : productTitle + ' - ' + (variant.title || '');
      adminDisplayLines.push({ title, quantity: 1, sku: variant.sku != null ? variant.sku : '' });
    });
    
    // Add selected add-ons
    // FoxSell format: variantId, quantity, type, properties (NO category field for add-ons)
    this.selectedAddOns.forEach(variantId => {
      selection.push({
        variantId: parseInt(variantId, 10),
        quantity: 1,
        type: "addOns",
        properties: {}
      });
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => Number(v.id) === Number(variantId));
      });
      if (addOnCard) {
        const productTitle = getProductTitle(addOnCard);
        const variants = JSON.parse(addOnCard.dataset.variants || '[]');
        const variant = variants.find(v => Number(v.id) === Number(variantId));
        const title = variant && variants.length > 1 ? productTitle + ' - ' + (variant.title || '') : productTitle;
        adminDisplayLines.push({ title, quantity: 1, sku: variant && variant.sku != null ? variant.sku : '' });
      }
    });

    // Validate selection - check if each category meets its minimum
    const categoryStats = new Map();
    
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const categoryMinimum = 1;
      const variantKey = `${categoryTitle}-${card.dataset.productId}`;
      const hasSelection = card.dataset.requiredInBundle === 'true' || !!this.selectedVariants.get(variantKey);
      
      if (!categoryStats.has(categoryTitle)) {
        categoryStats.set(categoryTitle, { minimum: categoryMinimum, selected: 0 });
      }
      
      if (hasSelection) {
        categoryStats.get(categoryTitle).selected++;
      }
    });
    
    const hasCategories = categoryStats.size > 0;
    const allCategoriesMeetMinimum = hasCategories && Array.from(categoryStats.values()).every(
      stats => stats.selected >= stats.minimum
    );
    
    if (!allCategoriesMeetMinimum) {
      return false;
    }

    // Create unique bundle ID
    const uniqueBundleId = `${this.bundleConfig.bundle_id}_${Date.now()}`;
    
    if (!this.productForm) {
      return false;
    }
    
    // Ensure variant ID input exists and is set
    if (this.variantIdInput) {
      this.variantIdInput.disabled = false;
      this.variantIdInput.value = this.bundleVariantId;
    } else {
      // Create variant ID input if it doesn't exist
      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "id";
      hiddenInput.value = this.bundleVariantId;
      this.productForm.appendChild(hiddenInput);
      this.variantIdInput = hiddenInput;
    }
    
    // Update or create bundle ID input
    let bundleIdInput = this.productForm.querySelector('.foxsell-bundle-id-input');
    if (!bundleIdInput) {
      bundleIdInput = document.createElement("input");
      bundleIdInput.type = "hidden";
      bundleIdInput.name = "properties[__foxsell:dynamic_add_on_bundle_id]";
      bundleIdInput.className = "foxsell-bundle-id-input";
      this.productForm.appendChild(bundleIdInput);
    }
    bundleIdInput.value = uniqueBundleId;

    // Update or create bundle items input
    let bundleItemsInput = this.productForm.querySelector('.foxsell-bundle-items-input');
    if (!bundleItemsInput) {
      bundleItemsInput = document.createElement("input");
      bundleItemsInput.type = "hidden";
      bundleItemsInput.name = "properties[__foxsell:dynamic_add_on_bundle_items]";
      bundleItemsInput.className = "foxsell-bundle-items-input";
      this.productForm.appendChild(bundleItemsInput);
    }
    bundleItemsInput.value = JSON.stringify(selection);

    // Calculate and store total compare price
    const totalComparePrice = this.calculateTotalComparePrice();
    let comparePriceInput = this.productForm.querySelector('.foxsell-bundle-compare-price-input');
    if (!comparePriceInput) {
      comparePriceInput = document.createElement("input");
      comparePriceInput.type = "hidden";
      comparePriceInput.name = "properties[__foxsell:bundle_compare_price]";
      comparePriceInput.className = "foxsell-bundle-compare-price-input";
      this.productForm.appendChild(comparePriceInput);
    }
    comparePriceInput.value = totalComparePrice;

    // Single human-readable property for order admin (key starts with _ so theme hides in cart)
    const adminValue = adminDisplayLines
      .map((item) => {
        const skuLine = item.sku !== '' ? `\nSKU: ${item.sku}` : '';
        return `${item.title}\nQuantity: ${item.quantity}${skuLine}`;
      })
      .join('\n\n');
    this.productForm.querySelectorAll('.bundle-admin-display-input').forEach((el) => el.remove());
    if (adminDisplayLines.length > 0) {
      const adminInput = document.createElement('input');
      adminInput.type = 'hidden';
      adminInput.name = 'properties[_Bundle contents]';
      adminInput.value = adminValue;
      adminInput.className = 'bundle-admin-display-input';
      this.productForm.appendChild(adminInput);
    }

    return true;
  }

  addToCart() {
    // Build selection from regular products (not add-ons)
    // Only include cards that have actual selections
    const regularProductCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle !== 'add-ons'
    );
    
    const regularSelection = [];
    
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const productId = card.dataset.productId;
      const variantKey = `${categoryTitle}-${productId}`;
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      const titleElement = card.querySelector('.fsb-whats-in-box__product-title');
      const productTitle = titleElement ? titleElement.textContent.trim() : '';
      const variants = JSON.parse(card.dataset.variants || '[]');

      if (card.dataset.requiredInBundle === 'true') {
        const combinedPriceCents = parseInt(card.dataset.combinedPrice, 10) || 0;
        const pricePerVariant = variants.length > 0 ? combinedPriceCents / variants.length / 100 : 0;
        variants.forEach(variant => {
          regularSelection.push({
            title: `${productTitle}${variants.length === 1 ? '' : ' - ' + variant.title}`,
            category: categoryTitle,
            variantId: parseInt(variant.id, 10),
            type: 'product',
            quantity: 1,
            price: pricePerVariant,
            sku: variant.sku != null ? variant.sku : '',
            properties: {}
          });
        });
        return; // Done with this card; continue to next
      }

      const variant = this.selectedVariants.get(variantKey);
      if (!variant) return;

      const variantCount = variants.length;
      const basePrice = variant.price || 0;
      let bundlePrice = basePrice;
      if (this.isDynamicPricing && this.discountPercentage > 0) {
        bundlePrice = basePrice * (1 - this.discountPercentage / 100);
      } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
        const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
        bundlePrice = parseFloat(appVariants[appVariantKey]) * 100;
      }
      
      regularSelection.push({
        title: `${productTitle}${variantCount === 1 ? '' : ' - ' + variant.title}`,
        category: categoryTitle,
        variantId: parseInt(variant.id, 10),
        type: 'product',
        quantity: 1,
        price: bundlePrice / 100,
        sku: variant.sku != null ? variant.sku : '',
        properties: {}
      });
    });

    // Check if we have at least one selection (category minimums are already checked by button state)
    if (regularSelection.length === 0) {
      this.showError('Please select at least one product');
      return;
    }
    
    // Build selection from selected add-ons
    const addOnSelection = [];
    this.selectedAddOns.forEach(variantId => {
      // Find the card that has this variant
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => v.id === variantId);
      });
      
      if (addOnCard) {
        const titleElement = addOnCard.querySelector('.fsb-add-ons__product-title');
        const productTitle = titleElement ? titleElement.textContent.trim() : '';
        const variants = JSON.parse(addOnCard.dataset.variants || '[]');
        const variant = variants.find(v => v.id === variantId);
        const appVariants = JSON.parse(addOnCard.dataset.appVariants || '{}');
        
        if (variant) {
          // Calculate bundle price for add-on
          let bundlePrice;
          
          // First try data attributes (from Liquid calculations)
          if (addOnCard.dataset.price) {
            bundlePrice = parseFloat(addOnCard.dataset.price) * 100;
          } else {
            // Calculate using same logic as regular products
            const basePrice = variant.price || 0;
            bundlePrice = basePrice;
            if (this.isDynamicPricing && this.discountPercentage > 0) {
              bundlePrice = basePrice * (1 - this.discountPercentage / 100);
            } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
              const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
              bundlePrice = parseFloat(appVariants[appVariantKey]) * 100;
            }
          }
          
          const variantCount = variants.length;
          
          addOnSelection.push({
            title: `${productTitle}${variantCount === 1 ? '' : ' - ' + variant.title}`,
            variantId: parseInt(variant.id, 10),
            type: 'addOns',
            quantity: 1,
            price: bundlePrice / 100, // Convert cents to dollars for FoxSell
            sku: variant.sku != null ? variant.sku : '',
            properties: {}
          });
        }
      }
    });
    
    // Combine regular products and add-ons
    const selection = [...regularSelection, ...addOnSelection];

    // Calculate total bundle price and compare price for the FoxSell properties
    const totalBundlePrice = selection.reduce((total, item) => total + (item.price * item.quantity), 0);
    
    // Calculate total compare price (regular prices before bundle discount)
    let totalComparePrice = 0;
    regularSelection.forEach(item => {
      const card = regularProductCards.find(c => {
        if (c.dataset.requiredInBundle === 'true') {
          const variants = JSON.parse(c.dataset.variants || '[]');
          return variants.some(v => Number(v.id) === Number(item.variantId));
        }
        const variant = this.selectedVariants.get(`${c.dataset.categoryTitle}-${c.dataset.productId}`);
        return variant && Number(variant.id) === Number(item.variantId);
      });
      if (card) {
        if (card.dataset.requiredInBundle === 'true') {
          const variants = JSON.parse(card.dataset.variants || '[]');
          const variant = variants.find(v => Number(v.id) === Number(item.variantId));
          if (variant) {
            const comparePrice = (variant.compare_at_price || variant.price || 0) / 100;
            totalComparePrice += comparePrice * item.quantity;
          }
        } else {
          const variant = this.selectedVariants.get(`${card.dataset.categoryTitle}-${card.dataset.productId}`);
          if (variant) {
            const comparePrice = (variant.compare_at_price || variant.price || 0) / 100;
            totalComparePrice += comparePrice * item.quantity;
          }
        }
      }
    });
    
    // Add add-on compare prices
    addOnSelection.forEach(item => {
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => Number(v.id) === Number(item.variantId));
      });
      if (addOnCard) {
        // Use data-compare-price if available
        if (addOnCard.dataset.comparePrice) {
          totalComparePrice += parseFloat(addOnCard.dataset.comparePrice) * item.quantity;
        } else {
          const variants = JSON.parse(addOnCard.dataset.variants || '[]');
          const variant = variants.find(v => Number(v.id) === Number(item.variantId));
          if (variant) {
            const comparePrice = (variant.compare_at_price || variant.price || 0) / 100;
            totalComparePrice += comparePrice * item.quantity;
          }
        }
      }
    });

    // Create unique bundle ID
    const uniqueBundleId = `${this.bundleConfig.bundle_id}__${Date.now()}`;

    const displayProperties = selection.reduce((acc, item) => {
      acc[item.title] = (acc[item.title] || 0) + item.quantity;
      return acc;
    }, {});

    // Human-readable property for order admin (key starts with _ so theme hides in cart)
    const adminBundleValue = selection
      .map((item) => {
        const sku = item.sku != null && item.sku !== '' ? item.sku : '—';
        return `${item.title}\nQuantity: ${item.quantity}\nSKU: ${sku}`;
      })
      .join('\n\n');
    
    // Create FoxSell-compatible bundle items format
    // Category is only included for regular products, not add-ons (per FoxSell's format)
    const foxsellBundleItems = selection.map(item => {
      const bundleItem = {
        variantId: item.variantId,
        quantity: item.quantity,
        type: item.type,
        properties: item.properties || {}
      };
      // Only include category for regular products (FoxSell doesn't use category for addOns)
      if (item.type === 'product' && item.category) {
        bundleItem.category = item.category;
      }
      return bundleItem;
    });

    // Format data for bundle variant add to cart
    const formData = {
      items: [{
        id: this.bundleVariantId,
        quantity: 1,
        properties: {
          '__foxsell:dynamic_add_on_bundle_id': uniqueBundleId,
          '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(foxsellBundleItems),
          '__foxsell:bundle_price': totalBundlePrice.toFixed(2),
          '__foxsell:bundle_compare_price': totalComparePrice.toFixed(2),
          '_Bundle contents': adminBundleValue,
          ...Object.keys(displayProperties).reduce((acc, key) => {
            acc[key] = `x${displayProperties[key]}`;
            return acc;
          }, {})
        }
      }],
      sections: ['cart-drawer', 'cart-icon-bubble']
    };

    if (this.blockSettings.custom_loading_class) {
      this.addToCartButton.classList.add(this.blockSettings.custom_loading_class);
    }

    // Add to cart using Shopify AJAX API
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === 422) {
          throw new Error(data.description || 'Error adding items to cart');
        }

        this.updateCartUI(data);
      })
      .catch(error => {
        this.showError(error.message || 'Error adding items to cart');
      });
  }

  updateCartUI(response) {
    if (this.blockSettings.custom_loading_class && this.addToCartButton) {
      this.addToCartButton.classList.remove(this.blockSettings.custom_loading_class);
    }

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      try {
        cart.renderContents(response);
        cart.open();
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      } catch (e) {
        window.location.href = window?.routes?.cart_url || '/cart';
      }
    } else {
      window.location.href = window?.routes?.cart_url || '/cart';
    }
  }

  async updateCartAttribute() {
    if (!this.bundleProductId) {
      return;
    }

    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const cart = await cartResponse.json();
      const existingValue = cart?.attributes?.foxsell_bundle_id;
      let bundleIds = [];

      if (existingValue) {
        // Keep older carts working if the attribute is still stored as a JSON string.
        try {
          const parsed = JSON.parse(existingValue);
          bundleIds = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
        } catch (error) {
          bundleIds = String(existingValue)
            .split('|')
            .map((value) => value.trim())
            .filter(Boolean);
        }
      }

      const nextBundleId = String(this.bundleProductId);
      if (!bundleIds.includes(nextBundleId)) {
        bundleIds.push(nextBundleId);
      }

      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            foxsell_bundle_id: `|${bundleIds.join('|')}|`
          }
        })
      });
    } catch (error) {
      console.error('[WhatsInBox] Error updating cart attribute:', error);
    }
  }

  updateAddToCartButtonState() {
    // Try to find the button if not already found
    if (!this.addToCartButton) {
      this.addToCartButton = this.productForm ? this.productForm.querySelector('button[type="submit"]') : null;
      if (!this.addToCartButton) {
        // Try alternative selectors
        this.addToCartButton = document.querySelector('form[is="product-form"] button[type="submit"]') ||
                               document.querySelector('form.product-form button[type="submit"]') ||
                               document.querySelector('form[id^="product-form-"] button[type="submit"]');
      }
    }
    
    if (!this.addToCartButton) {
      return;
    }

    // Check regular products (excluding add-ons)
    const regularProductCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle !== 'add-ons'
    );
    
    // Check if each category meets its minimum selection requirement
    const categoryStats = new Map();
    
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const categoryMinimum = 1;
      const variantKey = `${categoryTitle}-${card.dataset.productId}`;
      const hasSelection = card.dataset.requiredInBundle === 'true' || !!this.selectedVariants.get(variantKey);
      
      if (!categoryStats.has(categoryTitle)) {
        categoryStats.set(categoryTitle, { minimum: categoryMinimum, selected: 0 });
      }
      
      if (hasSelection) {
        categoryStats.get(categoryTitle).selected++;
      }
    });
    
    const hasCategories = categoryStats.size > 0;
    const allCategoriesMeetMinimum = hasCategories && Array.from(categoryStats.values()).every(
      stats => stats.selected >= stats.minimum
    );

    // Check add-on minimum requirement
    const addOnCount = this.selectedAddOns.size;
    const meetsMinAddOns = addOnCount >= this.addOnLimits.minimum;

    // Priority 1: Check if all categories meet their minimum (or if there are no selections yet)
    if (!allCategoriesMeetMinimum) {
      this.addToCartButton.textContent = 'Please Finish Selecting Products';
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add('fsb__atc-warning');
    } 
    // Priority 2: If all category minimums met, check add-ons (if add-ons are required)
    else if (this.addOnLimits.minimum > 0) {
      if (!meetsMinAddOns) {
        this.addToCartButton.textContent = `Please select at least ${this.addOnLimits.minimum} add-on${this.addOnLimits.minimum > 1 ? 's' : ''}`;
        this.addToCartButton.disabled = true;
        this.addToCartButton.classList.add('fsb__atc-warning');
      } else {
        // Add-ons minimum met - enable button and show price
        this.addToCartButton.classList.remove('fsb__atc-warning');
        this.updateAddToCartButtonWithPrice();
      }
    }
    // All category minimums met and no add-ons required - enable button and show price
    else {
      this.addToCartButton.classList.remove('fsb__atc-warning');
      this.updateAddToCartButtonWithPrice();
    }
  }
  
  /**
   * Update the add-to-cart button with price display
   * Shows total bundle price with compare price if discount applies
   */
  updateAddToCartButtonWithPrice() {
    if (!this.addToCartButton) return;
    
    // Note: Warning class is managed by updateAddToCartButtonState() - don't modify it here
    
    // Calculate total prices (reuse the same logic from updateTotalBundlePrice)
    let totalRegularPrice = 0; // Regular prices (for compare)
    let totalBundlePrice = 0; // Bundle prices (current price)
    
    // Get regular product cards (excluding add-ons)
    const regularProductCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle !== 'add-ons'
    );
    
    // Calculate prices for regular products
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const productId = card.dataset.productId;
      const variantKey = `${categoryTitle}-${productId}`;

      if (card.dataset.requiredInBundle === 'true') {
        const combinedCompare = parseInt(card.dataset.combinedComparePrice, 10) || 0;
        const combinedPrice = parseInt(card.dataset.combinedPrice, 10) || 0;
        totalRegularPrice += combinedCompare;
        totalBundlePrice += combinedPrice;
        return;
      }

      const variants = JSON.parse(card.dataset.variants || '[]');
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      let variant = this.selectedVariants.get(variantKey);
      
      if (!variant) return;
      
      const comparePrice = variant.compare_at_price || variant.price || 0;
      totalRegularPrice += comparePrice;
      
      const basePrice = variant.price || 0;
      let bundlePrice = basePrice;
      if (this.isDynamicPricing && this.discountPercentage > 0) {
        bundlePrice = basePrice * (1 - this.discountPercentage / 100);
      } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
        const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
        bundlePrice = parseFloat(appVariants[appVariantKey]) * 100;
      }
      totalBundlePrice += bundlePrice;
    });
    
    // Calculate prices for selected add-ons
    this.selectedAddOns.forEach(variantId => {
      // Find the card that has this variant
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => v.id === variantId);
      });
      
      if (addOnCard) {
        // For add-ons, use prices from data attributes if available (calculated in Liquid)
        // data-compare-price and data-price are in dollars, convert to cents
        if (addOnCard.dataset.comparePrice && addOnCard.dataset.price) {
          const comparePrice = parseFloat(addOnCard.dataset.comparePrice) * 100;
          const bundlePrice = parseFloat(addOnCard.dataset.price) * 100;
          
          totalRegularPrice += comparePrice;
          totalBundlePrice += bundlePrice;
        } else {
          // Fallback for non-split products: use variant prices with dynamic pricing
          const variants = JSON.parse(addOnCard.dataset.variants || '[]');
          const appVariants = JSON.parse(addOnCard.dataset.appVariants || '{}');
          const variant = variants.find(v => v.id === variantId);
          
          if (variant) {
            const comparePrice = variant.compare_at_price || variant.price || 0;
            totalRegularPrice += comparePrice;
            
            // Calculate bundle price using discount percentage if dynamic pricing is enabled
            const basePrice = variant.price || 0;
            let bundlePrice = basePrice;
            if (this.isDynamicPricing && this.discountPercentage > 0) {
              bundlePrice = basePrice * (1 - this.discountPercentage / 100);
            } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
              const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
              bundlePrice = parseFloat(appVariants[appVariantKey]) * 100;
            }
            totalBundlePrice += bundlePrice;
          }
        }
      }
    });
    
    // Format prices
    const formattedRegularPrice = this.formatPrice(totalRegularPrice);
    const formattedBundlePrice = this.formatPrice(totalBundlePrice);
    
    // Check if we should show compare price (bundle price is less than compare price)
    const showCompare = totalBundlePrice < totalRegularPrice && totalRegularPrice > 0;
    
    // Build button text with price
    let buttonText = this.blockSettings.add_to_cart_text || 'Add to Cart';
    
    if (showCompare) {
      // Show button text with current price and compare price
      buttonText = `${buttonText} - ${formattedBundlePrice} <span class="fsb-button-price-compare">${formattedRegularPrice}</span>`;
    } else {
      // Show button text with current price only
      buttonText = `${buttonText} - ${formattedBundlePrice}`;
    }
    
    this.addToCartButton.innerHTML = buttonText;
    this.addToCartButton.disabled = false;
  }

  bindEvents() {
    // Handle option selection for regular products
    this.container.addEventListener('click', (e) => {
      // First check for variant option clicks (for both regular and add-on products)
      const optionValue = e.target.closest('[data-value]');
      if (optionValue && !optionValue.disabled) {
        // Check both regular and add-on card classes
        const card = optionValue.closest('.fsb-whats-in-box__product-card') ||
                     optionValue.closest('.fsb-add-ons__product-card');
        const isAddOn = card && card.dataset.categoryTitle === 'add-ons';
        
        // For add-ons, update variant selection but don't auto-select the add-on
        if (isAddOn) {
          this.selectOptionValue(optionValue);
          // After selecting variant, allow the add-on to be selected
          return;
        } else {
          // Regular products - normal behavior
          this.selectOptionValue(optionValue);
          return;
        }
      }
      
      // Handle add-on button clicks
      const addOnButton = e.target.closest('.fsb-add-ons__add-button');
      if (addOnButton) {
        const addOnCard = addOnButton.closest('.fsb-add-ons__product-card') ||
                          addOnButton.closest('.fsb-whats-in-box__product-card');
        if (addOnCard && addOnCard.dataset.categoryTitle === 'add-ons') {
          this.toggleAddOnSelection(addOnCard);
        }
      }
      
      // Handle accordion header clicks for regular products (not add-ons)
      const regularProductHeader = e.target.closest('.fsb-whats-in-box__product-header');
      if (regularProductHeader) {
        const card = regularProductHeader.closest('.fsb-whats-in-box__product-card');
        // Only handle if it's not an add-on
        if (card && card.dataset.categoryTitle !== 'add-ons') {
          // Don't prevent default - let the setupAccordions handler manage it
          // But make sure we don't interfere with other clicks
          if (e.target.closest('img') || e.target.closest('.fsb-whats-in-box__quantity-badge')) {
            return;
          }
        }
      }

      // Handle add-on card clicks to switch gallery image
      const addOnCard = e.target.closest('.fsb-add-ons__product-card');
      if (addOnCard && addOnCard.dataset.categoryTitle === 'add-ons') {
        
        // Get the variant for this add-on card
        const variants = JSON.parse(addOnCard.dataset.variants || '[]');
        const appVariants = JSON.parse(addOnCard.dataset.appVariants || '{}');
        
        let variant = null;
        
        // For split products, each card has only one variant
        if (variants.length === 1) {
          variant = variants[0];
        } else {
          // Multi-variant product - get the selected variant from the card
          const cardKey = `add-ons-${addOnCard.dataset.productId}`;
          variant = this.selectedVariants.get(cardKey);
          
          // If no variant selected yet, find first available one
          if (!variant) {
            variant = variants.find(v => {
              if (!v.available) return false;
              
              // Check if variant exists in appVariants (for pricing info)
              const variantGid = `gid://shopify/ProductVariant/${v.id}`;
              const variantIdString = String(v.id);
              
              const hasPricing = appVariants && Object.keys(appVariants).length > 0
                ? (variantGid in appVariants || variantIdString in appVariants || v.id in appVariants)
                : true;
              
              return hasPricing;
            });
            
            // Fallback: just get first available
            if (!variant) {
              variant = variants.find(v => v.available);
            }
          }
        }
        
        // Update gallery image if variant found
        if (variant) {
          this.updateGalleryImage(variant);
        }
      }
    });

    if (this.productForm) {
      this.productForm.addEventListener('submit', (e) => {
        const success = this.updateLineItemProperties();
        if (!success) {
          e.preventDefault();
          e.stopPropagation();
          this.showError('Please select options for all products');
          return false;
        }
      }, true); // Use capture phase to run before theme's handler

      this.productForm.addEventListener('variant:added', () => {
        this.updateCartAttribute();
      });
    }
  }
  
  toggleAddOnSelection(card) {
    const categoryTitle = card.dataset.categoryTitle;
    if (categoryTitle !== 'add-ons') return;
    
    // Get variants from the card data (for split products, each card has its own variant)
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    
    // For split products, each card should have only one variant
    // For multi-variant products, get the currently selected variant
    let variant = null;
    
    // Check if this is a split product (single variant per card)
    if (variants.length === 1) {
      variant = variants[0];
    } else {
      // Multi-variant product - get the selected variant from the card
      const cardKey = `add-ons-${card.dataset.productId}`;
      variant = this.selectedVariants.get(cardKey);
      
      // If no variant selected yet, find first available one
      if (!variant) {
        variant = variants.find(v => {
          if (!v.available) return false;
          
          // Check if variant exists in appVariants (for pricing info)
          const variantGid = `gid://shopify/ProductVariant/${v.id}`;
          const variantIdString = String(v.id);
          
          const hasPricing = appVariants && Object.keys(appVariants).length > 0
            ? (variantGid in appVariants || variantIdString in appVariants || v.id in appVariants)
            : true;
          
          return hasPricing;
        });
        
        // Fallback: just get first available
        if (!variant) {
          variant = variants.find(v => v.available);
        }
        
        if (variant) {
          // Store the variant for this card
          this.selectedVariants.set(cardKey, variant);
          this.selectedOptions.set(cardKey, variant.options || []);
        }
      }
    }
    
      if (!variant) {
        this.showError('This add-on is not available');
        return;
      }
    
    const variantId = variant.id;
    const isSelected = this.selectedAddOns.has(variantId);
    
    // Check max limit
    if (!isSelected && this.addOnLimits.maximum > 0 && this.selectedAddOns.size >= this.addOnLimits.maximum) {
      this.showError(`Maximum ${this.addOnLimits.maximum} add-on${this.addOnLimits.maximum > 1 ? 's' : ''} allowed`);
      return;
    }
    
    // Toggle selection
    if (isSelected) {
      this.selectedAddOns.delete(variantId);
      card.classList.remove('fsb-add-ons__product-card--selected');
    } else {
      this.selectedAddOns.add(variantId);
      card.classList.add('fsb-add-ons__product-card--selected');
    }
    
    // Update all add-on buttons to reflect their current selection state
    this.updateAllAddOnButtons();
    
    // Get selected add-on titles for logging
    const selectedAddOnTitles = [];
    this.selectedAddOns.forEach(variantId => {
      // Find the card that has this variant
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => v.id === variantId);
      });
      
      if (addOnCard) {
        const cardVariants = JSON.parse(addOnCard.dataset.variants || '[]');
        
        // Find the specific variant
        const selectedVariant = cardVariants.find(v => v.id === variantId);
        
        if (selectedVariant) {
          // Get title from DOM element (already includes variant name for split products)
          const cardTitle = addOnCard.querySelector('.fsb-whats-in-box__product-title') ||
                            addOnCard.querySelector('.fsb-add-ons__product-title');
          const titleText = cardTitle ? cardTitle.textContent.trim() : null;
          
          if (titleText) {
            selectedAddOnTitles.push(titleText);
          } else {
            // Fallback: construct title from data attributes if DOM title not available
            const productTitle = addOnCard.dataset.productTitle || '';
            const title = cardVariants.length === 1 
              ? productTitle 
              : `${productTitle} - ${selectedVariant.title}`;
            selectedAddOnTitles.push(title);
          }
        }
      }
    });
    
    // Update total bundle price display
    this.updateTotalBundlePrice();
    
    // Update button state
    this.updateAddToCartButtonState();
    
    // Update line item properties
    this.updateLineItemProperties();
  }
  
  updateAddOnButton(card, variant, appVariants, isSelected) {
    const button = card.querySelector('.fsb-add-ons__add-button');
    if (!button) return;
    
    const buttonText = button.querySelector('.fsb-add-ons__add-button-text');
    const buttonPrice = button.querySelector('.fsb-add-ons__add-button-price');
    
    if (!buttonText || !buttonPrice) return;
    
    // Update variant ID on button
    if (variant && variant.id) {
      button.dataset.variantId = variant.id;
    }
    
    // Update button text - show "Added" when selected, "Add" when not
    buttonText.textContent = isSelected ? 'Added' : 'Add';
  }
  
  updateAllAddOnButtons() {
    // Update all add-on buttons to reflect their current selection state
    const addOnCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle === 'add-ons'
    );
    
    addOnCards.forEach(card => {
      const variants = JSON.parse(card.dataset.variants || '[]');
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      
      if (variants.length === 0) return;
      
      // For split products, use the first (and only) variant
      // For non-split products, get the selected variant or first available
      let variant = variants[0];
      if (variants.length > 1) {
        const cardKey = `add-ons-${card.dataset.productId}`;
        variant = this.selectedVariants.get(cardKey);
        if (!variant) {
          variant = variants.find(v => v.available && (v.id in appVariants || `gid://shopify/ProductVariant/${v.id}` in appVariants)) || 
                    variants.find(v => v.available) || 
                    variants[0];
        }
      }
      
      if (variant) {
        const variantId = parseInt(variant.id, 10);
        const isSelected = this.selectedAddOns.has(variantId);
        this.updateAddOnButton(card, variant, appVariants, isSelected);
      }
    });
  }
  
  setupAddOnButtons() {
    // Initialize all add-on buttons with prices
    const addOnCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle === 'add-ons'
    );
    
    addOnCards.forEach((card) => {
      const variants = JSON.parse(card.dataset.variants || '[]');
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      
      if (variants.length === 0) return;
      
      // For split products, use the first (and only) variant
      // For non-split products, find the first available variant
      let variant = variants[0];
      if (variants.length > 1) {
        variant = variants.find(v => v.available && (v.id in appVariants || `gid://shopify/ProductVariant/${v.id}` in appVariants)) || 
                  variants.find(v => v.available) || 
                  variants[0];
      }
      
      if (variant) {
        const variantId = parseInt(variant.id, 10);
        const isSelected = this.selectedAddOns.has(variantId);
        this.updateAddOnButton(card, variant, appVariants, isSelected);
      }
    });
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
      setTimeout(() => {
        this.errorMessage.style.display = 'none';
      }, 3000);
    }
  }

  /**
   * Calculate and update the total bundle price display
   * Includes all selected products and add-ons
   */
  calculateTotalComparePrice() {
    // Get regular bundle products (excluding add-ons)
    const regularProductCards = Array.from(this.productCards)
      .filter(card => card.dataset.categoryTitle !== 'add-ons');
    
    let totalComparePrice = 0;
    
    // Calculate compare prices for regular products
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const productId = card.dataset.productId;
      const variantKey = `${categoryTitle}-${productId}`;

      if (card.dataset.requiredInBundle === 'true') {
        const combinedCompare = parseInt(card.dataset.combinedComparePrice, 10) || 0;
        totalComparePrice += combinedCompare;
        return;
      }

      const variant = this.selectedVariants.get(variantKey);
      if (!variant) return;

      const comparePrice = variant.compare_at_price || variant.price || 0;
      totalComparePrice += comparePrice;
    });
    
    // Calculate compare prices for selected add-ons
    this.selectedAddOns.forEach(variantId => {
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => v.id === variantId);
      });
      
      if (addOnCard) {
        // For add-ons, use prices from data attributes if available (calculated in Liquid)
        if (addOnCard.dataset.comparePrice) {
          const comparePrice = parseFloat(addOnCard.dataset.comparePrice) * 100; // Convert to cents
          totalComparePrice += comparePrice;
        } else {
          // Fallback for non-split products: use variant prices
          const variants = JSON.parse(addOnCard.dataset.variants || '[]');
          const variant = variants.find(v => v.id === variantId);
          
          if (variant) {
            // For add-ons, use compare_at_price or price
            const comparePrice = variant.compare_at_price || variant.price || 0;
            totalComparePrice += comparePrice;
          }
        }
      }
    });
    
    return totalComparePrice;
  }

  updateTotalBundlePrice() {
    if (!this.priceListElement) {
      // Try to find it again in case it wasn't available during initialization
      this.priceListElement = document.querySelector('[data-product-price-list]');
      if (!this.priceListElement) return;
    }
    
    // Get regular product cards (excluding add-ons)
    const regularProductCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle !== 'add-ons'
    );
    
    // Check if each category meets its minimum selection requirement
    const categoryStats = new Map();
    
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const categoryMinimum = 1;
      const variantKey = `${categoryTitle}-${card.dataset.productId}`;
      const hasSelection = card.dataset.requiredInBundle === 'true' || !!this.selectedVariants.get(variantKey);
      
      if (!categoryStats.has(categoryTitle)) {
        categoryStats.set(categoryTitle, { minimum: categoryMinimum, selected: 0 });
      }
      
      if (hasSelection) {
        categoryStats.get(categoryTitle).selected++;
      }
    });
    
    const hasCategories = categoryStats.size > 0;
    const allCategoriesMeetMinimum = hasCategories && Array.from(categoryStats.values()).every(
      stats => stats.selected >= stats.minimum
    );
    
    // Check add-on minimum requirement
    const addOnCount = this.selectedAddOns.size;
    const meetsMinAddOns = addOnCount >= this.addOnLimits.minimum;
    
    // Check if all requirements are met
    const requirementsMetForAddOns = this.addOnLimits.minimum === 0 || meetsMinAddOns;
    const allRequirementsMet = allCategoriesMeetMinimum && requirementsMetForAddOns;
    
    // If requirements are NOT met, reset to default "From" pricing
    if (!allRequirementsMet) {
      this.resetPriceDisplay();
      return;
    }
    
    let totalRegularPrice = 0; // Regular prices (for compare)
    let totalBundlePrice = 0; // Bundle prices (current price)
    
    // Calculate prices for regular products
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const productId = card.dataset.productId;
      const variantKey = `${categoryTitle}-${productId}`;

      if (card.dataset.requiredInBundle === 'true') {
        const combinedCompare = parseInt(card.dataset.combinedComparePrice, 10) || 0;
        const combinedPrice = parseInt(card.dataset.combinedPrice, 10) || 0;
        totalRegularPrice += combinedCompare;
        totalBundlePrice += combinedPrice;
        return;
      }

      const variants = JSON.parse(card.dataset.variants || '[]');
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      let variant = this.selectedVariants.get(variantKey);
      
      if (!variant) return;
      
      const comparePrice = variant.compare_at_price || variant.price || 0;
      totalRegularPrice += comparePrice;
      
      const basePrice = variant.price || 0;
      let bundlePrice = basePrice;
      if (this.isDynamicPricing && this.discountPercentage > 0) {
        bundlePrice = basePrice * (1 - this.discountPercentage / 100);
      } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
        const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
        bundlePrice = parseFloat(appVariants[appVariantKey]) * 100;
      }
      totalBundlePrice += bundlePrice;
    });
    
    // Calculate prices for selected add-ons
    this.selectedAddOns.forEach(variantId => {
      // Find the card that has this variant
      const addOnCard = Array.from(this.productCards).find(card => {
        if (card.dataset.categoryTitle !== 'add-ons') return false;
        const cardVariants = JSON.parse(card.dataset.variants || '[]');
        return cardVariants.some(v => v.id === variantId);
      });
      
      if (addOnCard) {
        // For add-ons, use prices from data attributes if available (calculated in Liquid)
        // data-compare-price and data-price are in dollars, convert to cents
        if (addOnCard.dataset.comparePrice && addOnCard.dataset.price) {
          const comparePrice = parseFloat(addOnCard.dataset.comparePrice) * 100;
          const bundlePrice = parseFloat(addOnCard.dataset.price) * 100;
          
          totalRegularPrice += comparePrice;
          totalBundlePrice += bundlePrice;
        } else {
          // Fallback for non-split products: use variant prices with dynamic pricing
          const variants = JSON.parse(addOnCard.dataset.variants || '[]');
          const appVariants = JSON.parse(addOnCard.dataset.appVariants || '{}');
          const variant = variants.find(v => v.id === variantId);
          
          if (variant) {
            const comparePrice = variant.compare_at_price || variant.price || 0;
            totalRegularPrice += comparePrice;
            
            // Calculate bundle price using discount percentage if dynamic pricing is enabled
            const basePrice = variant.price || 0;
            let bundlePrice = basePrice;
            if (this.isDynamicPricing && this.discountPercentage > 0) {
              bundlePrice = basePrice * (1 - this.discountPercentage / 100);
            } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
              const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
              bundlePrice = parseFloat(appVariants[appVariantKey]) * 100;
            }
            totalBundlePrice += bundlePrice;
          }
        }
      }
    });
    
    // Update the price display
    this.updatePriceListElement(totalRegularPrice, totalBundlePrice);
  }
  
  /**
   * Update the price list element with formatted prices
   * Uses data attributes on pre-rendered elements instead of templates
   * @param {number} regularPrice - Regular price in cents
   * @param {number} bundlePrice - Bundle price in cents
   */
  updatePriceListElement(regularPrice, bundlePrice) {
    if (!this.priceListElement) return;
    
    const formattedRegularPrice = this.formatPrice(regularPrice);
    const formattedBundlePrice = this.formatPrice(bundlePrice);
    
    // Check if we should show compare price (bundle price is less than regular price)
    const showCompare = bundlePrice < regularPrice && regularPrice > 0;
    
    // Calculate savings amount
    const savingsAmount = showCompare ? regularPrice - bundlePrice : 0;
    const formattedSavings = savingsAmount > 0 ? this.formatPrice(savingsAmount) : '';
    
    // Get the parent container for elements outside the price-list
    const priceContainer = this.priceListElement.closest('.product-meta__price-list-container');
    
    // Hide "Starting at" text when showing final price (it's in the parent container)
    const startingAtEl = priceContainer ? priceContainer.querySelector('[data-starting-at]') : null;
    if (startingAtEl) {
      startingAtEl.style.display = 'none';
    }
    
    // Update current price
    const currentPriceEl = this.priceListElement.querySelector('[data-price-current]');
    if (currentPriceEl) {
      currentPriceEl.textContent = formattedBundlePrice;
    }
    
    // Update compare price
    const comparePriceEl = this.priceListElement.querySelector('[data-price-compare]');
    const comparePriceWrapper = comparePriceEl?.closest('.price--compare');
    if (comparePriceEl) {
    if (showCompare) {
        comparePriceEl.textContent = formattedRegularPrice;
        if (comparePriceWrapper) comparePriceWrapper.style.display = '';
    } else {
        if (comparePriceWrapper) comparePriceWrapper.style.display = 'none';
      }
    }
    
    // Update savings badge (it's in the parent container, in the label-list)
    const savingsEl = priceContainer ? priceContainer.querySelector('[data-price-savings]') : null;
    if (savingsEl) {
      if (showCompare && formattedSavings) {
        savingsEl.textContent = `Save ${formattedSavings}`;
        savingsEl.style.display = '';
      } else {
        savingsEl.style.display = 'none';
      }
    }
  }
  
  /**
   * Reset price display to default "From" state
   * Called when requirements are no longer met (e.g., user removes a product)
   * Restores to original Liquid-rendered state
   */
  resetPriceDisplay() {
    if (!this.priceListElement) return;
    
    // Get the parent container for elements outside the price-list
    const priceContainer = this.priceListElement.closest('.product-meta__price-list-container');
    
    // Show "Starting at" / "From" text
    const startingAtEl = priceContainer ? priceContainer.querySelector('[data-starting-at]') : null;
    if (startingAtEl) {
      startingAtEl.style.display = '';
    }
    
    // Reset current price to original value
    const currentPriceEl = this.priceListElement.querySelector('[data-price-current]');
    if (currentPriceEl && this.originalPrice) {
      currentPriceEl.textContent = this.originalPrice;
    }
    
    // Restore compare price to original state (visible or hidden based on Liquid render)
    const comparePriceEl = this.priceListElement.querySelector('[data-price-compare]');
    const comparePriceWrapper = comparePriceEl?.closest('.price--compare');
    if (comparePriceWrapper) {
      if (this.originalComparePriceVisible) {
        comparePriceWrapper.style.display = '';
        if (comparePriceEl && this.originalComparePrice) {
          comparePriceEl.textContent = this.originalComparePrice;
        }
      } else {
        comparePriceWrapper.style.display = 'none';
      }
    }
    
    // Restore savings badge to original state
    const savingsEl = priceContainer ? priceContainer.querySelector('[data-price-savings]') : null;
    if (savingsEl) {
      if (this.originalSavingsVisible) {
        savingsEl.style.display = '';
        if (this.originalSavingsText) {
          savingsEl.textContent = this.originalSavingsText;
        }
      } else {
        savingsEl.style.display = 'none';
      }
    }
  }

  /**
   * Format price according to the current currency
   * @param {number} price - Price in cents
   * @returns {string} - Formatted price
   */
  formatPrice(cents) {
    if (typeof cents == 'string') { cents = cents.replace('.', ''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = this.currencyFormat;

    function defaultOption(opt, def) {
      return (typeof opt == 'undefined' ? def : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) { return 0; }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents = parts[1] ? (decimal + parts[1]) : '';

      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

  /**
   * Compute and display combined price/compare for required-in-bundle product (all variants).
   * Uses same logic as single-variant: dynamic pricing discount or appVariants (FoxSell) per variant, then sum.
   */
  updateRequiredInBundleProductPrice(card) {
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    let combinedCompareCents = 0;
    let combinedPriceCents = 0;

    variants.forEach(variant => {
      const comparePrice = variant.compare_at_price || variant.price || 0;
      combinedCompareCents += comparePrice;

      const basePrice = variant.price || 0;
      let bundlePriceCents = basePrice;
      if (this.isDynamicPricing && this.discountPercentage > 0) {
        bundlePriceCents = basePrice * (1 - this.discountPercentage / 100);
      } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
        const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
        bundlePriceCents = parseFloat(appVariants[appVariantKey]) * 100;
      }
      combinedPriceCents += bundlePriceCents;
    });

    card.dataset.combinedPrice = String(combinedPriceCents);
    card.dataset.combinedComparePrice = String(combinedCompareCents);

    const priceContainer = card.querySelector('.fsb-whats-in-box__product-price');
    if (!priceContainer) return;

    const comparePriceEl = priceContainer.querySelector('.fsb-whats-in-box__product-price-compare');
    const currentPriceEl = priceContainer.querySelector('.fsb-whats-in-box__product-price-current');

    if (currentPriceEl) {
      currentPriceEl.textContent = this.formatPrice(combinedPriceCents);
    }
    if (comparePriceEl && combinedCompareCents > 0 && combinedPriceCents < combinedCompareCents) {
      comparePriceEl.textContent = this.formatPrice(combinedCompareCents);
      comparePriceEl.style.display = '';
    } else if (comparePriceEl) {
      comparePriceEl.textContent = '';
      comparePriceEl.style.display = 'none';
    }
  }

  /**
   * Update the price display for a specific product
   * @param {HTMLElement} card - Product card element
   * @param {Object} variant - Selected variant
   * @param {Object} appVariants - App variants data
   */
  updateProductPrice(card, variant, appVariants) {
    if (!variant) return;

    // Check both regular and add-on price classes
    const priceContainer = card.querySelector('.fsb-whats-in-box__product-price') ||
                           card.querySelector('.fsb-add-ons__product-price');
    if (!priceContainer) return;

    // Use compare_at_price if available, otherwise fall back to regular price
    const comparePrice = variant.compare_at_price || variant.price; // Compare price from variant (in cents)
    const basePrice = variant.price || 0; // Base price from variant (in cents)
    
    // Calculate bundle price using discount percentage if dynamic pricing is enabled
    let bundlePriceCents = basePrice;
    if (this.isDynamicPricing && this.discountPercentage > 0) {
      // Apply discount percentage: basePrice * (1 - discountPercentage / 100)
      bundlePriceCents = basePrice * (1 - this.discountPercentage / 100);
    } else if (appVariants && variant.id in appVariants) {
      // Fallback to appVariants price if dynamic pricing is not enabled
      const bundlePrice = appVariants[variant.id];
      bundlePriceCents = parseFloat(bundlePrice) * 100;
    }
    
    // Check both regular and add-on price element classes
    const comparePriceEl = priceContainer.querySelector('.fsb-whats-in-box__product-price-compare') ||
                           priceContainer.querySelector('.fsb-add-ons__product-price-compare');
    const currentPriceEl = priceContainer.querySelector('.fsb-whats-in-box__product-price-current') ||
                           priceContainer.querySelector('.fsb-add-ons__product-price-current');
    
    // Show compare price (strikethrough) if bundle price is less than compare price
    if (comparePriceEl && comparePrice && bundlePriceCents < comparePrice) {
      comparePriceEl.textContent = this.formatPrice(comparePrice);
      comparePriceEl.style.display = '';
    } else if (comparePriceEl) {
      comparePriceEl.textContent = '';
      comparePriceEl.style.display = 'none';
    }
    
    // Show bundle price
    if (currentPriceEl) {
      currentPriceEl.textContent = this.formatPrice(bundlePriceCents);
    } else {
      // Fallback for old structure without separate elements
      priceContainer.textContent = this.formatPrice(bundlePriceCents);
    }
  }

  /**
   * Clear the price display for a product
   * @param {HTMLElement} card - Product card element
   */
  clearProductPrice(card) {
    // Check both regular and add-on price classes
    const priceContainer = card.querySelector('.fsb-whats-in-box__product-price') ||
                           card.querySelector('.fsb-add-ons__product-price');
    if (priceContainer) {
      // Check both regular and add-on price element classes
      const comparePriceEl = priceContainer.querySelector('.fsb-whats-in-box__product-price-compare') ||
                             priceContainer.querySelector('.fsb-add-ons__product-price-compare');
      const currentPriceEl = priceContainer.querySelector('.fsb-whats-in-box__product-price-current') ||
                             priceContainer.querySelector('.fsb-add-ons__product-price-current');
      
      if (comparePriceEl) comparePriceEl.textContent = '';
      if (currentPriceEl) {
        currentPriceEl.textContent = '';
      } else {
        priceContainer.textContent = '';
      }
    }
  }

  /**
   * Update the bundle pricing based on selected variants
   */
  updateBundlePricing() {
    const regularProductCards = Array.from(this.productCards).filter(
      card => card.dataset.categoryTitle !== 'add-ons'
    );
    const anyOutOfStock = regularProductCards.some(card => {
      if (card.dataset.requiredInBundle === 'true') return false;
      const variantKey = `${card.dataset.categoryTitle}-${card.dataset.productId}`;
      return !this.selectedVariants.get(variantKey);
    });

    if (anyOutOfStock) {
      this.hideBundlePrices();
      return;
    }

    let totalPrice = 0;
    regularProductCards.forEach(card => {
      const categoryTitle = card.dataset.categoryTitle;
      const productId = card.dataset.productId;
      const variantKey = `${categoryTitle}-${productId}`;

      if (card.dataset.requiredInBundle === 'true') {
        const combinedCents = parseInt(card.dataset.combinedPrice, 10) || 0;
        // combinedPrice is already discounted in updateRequiredInBundleProductPrice; use pre-discount
        // here so the single discount application below applies once (avoids double discount).
        const amount = this.isDynamicPricing && this.discountPercentage > 0
          ? (combinedCents / (1 - this.discountPercentage / 100)) / 100
          : combinedCents / 100;
        totalPrice += amount;
        return;
      }

      const variant = this.selectedVariants.get(variantKey);
      const appVariants = JSON.parse(card.dataset.appVariants || '{}');
      if (variant && variant.id in appVariants) {
        totalPrice += parseFloat(appVariants[variant.id]);
      }
    });

    // Apply discount if applicable
    if (totalPrice > 0) {
      const discountedPrice = this.discountPercentage > 0 ?
        totalPrice * (1 - this.discountPercentage / 100) : totalPrice;

      // Update price elements
      this.updateBundlePriceDisplay(totalPrice, discountedPrice);
    } else {
      this.hideBundlePrices();
    }
  }

  /**
   * Update the bundle price display with formatted prices
   * @param {number} originalPrice - Original price in cents
   * @param {number} discountedPrice - Discounted price in cents
   */
  updateBundlePriceDisplay(originalPrice, discountedPrice) {
    // Format prices
    const formattedOriginalPrice = this.formatPrice(originalPrice * 100);
    const formattedDiscountedPrice = this.formatPrice(discountedPrice * 100);

    // Update compare-at price elements if discount is applied
    if (this.discountPercentage > 0 && this.compareAtPriceElements.length > 0) {
      this.compareAtPriceElements.forEach(element => {
        element.textContent = formattedOriginalPrice;
        element.style.display = 'inline';
      });
    } else if (this.compareAtPriceElements.length > 0) {
      this.compareAtPriceElements.forEach(element => {
        element.style.display = 'none';
      });
    }

    // Update price elements
    if (this.priceElements.length > 0) {
      this.priceElements.forEach(element => {
        element.textContent = formattedDiscountedPrice;
        element.style.display = 'inline';
      });
    }
  }

  /**
   * Hide all bundle price elements
   */
  hideBundlePrices() {
    if (this.priceElements.length > 0) {
      this.priceElements.forEach(element => {
        element.style.display = 'none';
      });
    }

    if (this.compareAtPriceElements.length > 0) {
      this.compareAtPriceElements.forEach(element => {
        element.style.display = 'none';
      });
    }
  }

  // Modal Methods
  setupModal() {
    // Click handler for See Details buttons
    this.container.addEventListener('click', (e) => {
      const seeDetailsBtn = e.target.closest('[data-see-details-trigger]');
      if (seeDetailsBtn) {
        e.preventDefault();
        e.stopPropagation();
        const card = seeDetailsBtn.closest('.fsb-whats-in-box__product-card');
        if (card) {
          this.openModal(card);
        }
      }
    });

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.currentModal) {
        this.closeModal();
      }
    });
    
    // Setup event handlers for all pre-rendered modals
    const allModals = this.container.querySelectorAll('[data-product-modal]');
    allModals.forEach(modal => {
      // Close on overlay click
      const overlay = modal.querySelector('[data-modal-overlay]');
      if (overlay) {
        overlay.addEventListener('click', () => this.closeModal());
      }
      
      // Close on close button click
      const closeBtn = modal.querySelector('[data-modal-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeModal());
      }
      
      // Select button click handler
      const selectBtn = modal.querySelector('[data-modal-select]');
      if (selectBtn) {
        selectBtn.addEventListener('click', () => this.handleModalSelectOption());
      }
      
      // Remove button click handler
      const removeBtn = modal.querySelector('[data-modal-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.handleModalRemoveProduct());
      }
      
      // Variant option click handlers
      const variantBtns = modal.querySelectorAll('.fsb-modal-variant-option');
      variantBtns.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleModalOptionClick(e));
      });
      
      // FAQ toggle handlers
      const faqToggles = modal.querySelectorAll('[data-faq-toggle]');
      faqToggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
          const index = toggle.dataset.faqToggle;
          const content = modal.querySelector(`[data-faq-content="${index}"]`);
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          
          toggle.setAttribute('aria-expanded', !isOpen);
          if (content) {
            content.classList.toggle('fsb-whats-in-box__modal-faq-content--open', !isOpen);
          }
        });
      });
    });
    
    // Setup add-on "See Details" button handlers (for split products)
    const addonSeeDetailsBtns = this.container.querySelectorAll('[data-addon-see-details]');
    addonSeeDetailsBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const productId = btn.dataset.addonSeeDetails;
        const variantId = btn.dataset.addonVariantId || null;
        this.openAddonModal(productId, variantId);
      });
    });
    
    // Setup add-on modal trigger button handlers (for non-split products with variant selectors)
    const addonModalTriggerBtns = this.container.querySelectorAll('[data-addon-modal-trigger]');
    addonModalTriggerBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const productId = btn.dataset.productId;
        const card = btn.closest('.fsb-add-ons__product-card');
        this.openAddonModalWithVariants(productId, card);
      });
    });
    
    // Setup add-on checkmark handlers (for deselecting add-ons)
    const addonCheckmarks = this.container.querySelectorAll('.fsb-add-ons__checkmark');
    addonCheckmarks.forEach(checkmark => {
      checkmark.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = checkmark.closest('.fsb-add-ons__product-card');
        if (card && card.classList.contains('fsb-add-ons__product-card--selected')) {
          this.deselectAddOn(card);
        }
      });
    });
    
    // Setup event handlers for all pre-rendered add-on modals
    const addonModals = this.container.querySelectorAll('[data-addon-modal]');
    addonModals.forEach(modal => {
      // Close on overlay click
      const overlay = modal.querySelector('[data-modal-overlay]');
      if (overlay) {
        overlay.addEventListener('click', () => this.closeModal());
      }
      
      // Close on close button click
      const closeBtn = modal.querySelector('[data-modal-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeModal());
      }
      
      // Add button click handler (for split products)
      const addBtn = modal.querySelector('[data-addon-modal-add]');
      if (addBtn) {
        addBtn.addEventListener('click', () => this.handleAddonModalAdd());
      }
      
      // Remove button click handler (for split products)
      const removeBtn = modal.querySelector('[data-addon-modal-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.handleAddonModalRemove());
      }
      
      // Select button click handler (for non-split products with variant selectors)
      const selectBtn = modal.querySelector('[data-addon-modal-select]');
      if (selectBtn) {
        selectBtn.addEventListener('click', () => this.handleAddonModalSelectOption());
      }
      
      // Remove option button click handler (for non-split products)
      const removeOptionBtn = modal.querySelector('[data-addon-modal-remove-option]');
      if (removeOptionBtn) {
        removeOptionBtn.addEventListener('click', () => this.handleAddonModalRemoveOption());
      }
      
      // Variant option click handlers (for non-split products)
      const variantOptions = modal.querySelectorAll('.fsb-modal-variant-option');
      variantOptions.forEach(option => {
        option.addEventListener('click', (e) => this.handleAddonModalOptionClick(e, modal));
      });
      
      // FAQ toggle handlers
      const faqToggles = modal.querySelectorAll('[data-faq-toggle]');
      faqToggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
          const index = toggle.dataset.faqToggle;
          const content = modal.querySelector(`[data-faq-content="${index}"]`);
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          
          toggle.setAttribute('aria-expanded', !isOpen);
          if (content) {
            content.classList.toggle('fsb-whats-in-box__modal-faq-content--open', !isOpen);
          }
        });
      });
    });
  }

  openModal(card) {
    const productId = card.dataset.productId;
    const modal = this.container.querySelector(`[data-product-modal="${productId}"]`);
    
    if (!modal) return;

    // Store references
    this.currentModal = modal;
    this.currentModalCard = card;
    this.modalSelectedOptions = {};
    
    // Parse modal data from embedded JSON
    let variants = [];
    let productOptions = [];
    let variantImageMap = {};
    let variantsWithGallery = [];
    
    try {
      const variantsJson = modal.querySelector('[data-modal-variants-json]');
      const optionsJson = modal.querySelector('[data-modal-options-json]');
      const imageMapJson = modal.querySelector('[data-variant-image-map]');
      const galleryVariantsJson = modal.querySelector('[data-variants-with-gallery]');
      
      if (variantsJson) variants = JSON.parse(variantsJson.textContent);
      if (optionsJson) productOptions = JSON.parse(optionsJson.textContent);
      if (imageMapJson) variantImageMap = JSON.parse(imageMapJson.textContent);
      if (galleryVariantsJson) variantsWithGallery = JSON.parse(galleryVariantsJson.textContent);
    } catch (e) {
      // Silently fail, data may not be needed
    }
    
    // Store for later use
    this.modalVariants_data = variants;
    this.modalOptions_data = productOptions;
    this.modalVariantImageMap = variantImageMap;
    this.variantsWithGallery = variantsWithGallery;
    
    // Update modal price
    this.updateModalPrice(card, modal);
    
    // Check if this SPECIFIC product is already selected
    // Use product-specific key: categoryTitle-productId
    const categoryTitle = card.dataset.categoryTitle;
    // productId already declared above
    const variantKey = `${categoryTitle}-${productId}`;
    const selectedVariant = this.selectedVariants.get(variantKey);
    const isRequiredInBundle = this.currentModalCard && this.currentModalCard.dataset.requiredInBundle === 'true';
    
    const isSelected = isRequiredInBundle || !!selectedVariant;
    
    const removeBtn = modal.querySelector('[data-modal-remove]');
    if (removeBtn) {
      removeBtn.hidden = isRequiredInBundle || !isSelected;
    }
    
    const selectBtn = modal.querySelector('[data-modal-select]');
    if (selectBtn) {
      selectBtn.hidden = isRequiredInBundle;
      if (!isRequiredInBundle) {
        const optionsCount = productOptions.length;
        if (isSelected) {
          selectBtn.textContent = optionsCount > 1 ? 'Change Options' : 'Change Option';
        } else {
          selectBtn.textContent = optionsCount > 1 ? 'Choose Options' : 'Choose Option';
        }
      }
    }
    
    // Pre-select first available variant (or re-select the currently selected variant)
    if (isSelected) {
      // Re-select the currently selected options (selectedVariant already retrieved above)
      const selectedOpts = this.selectedOptions.get(variantKey) || [];
      
      productOptions.forEach((option, idx) => {
        const optionValue = selectedOpts[idx];
        if (optionValue) {
          this.modalSelectedOptions[option.name] = optionValue;
          
          // Update UI to show selected state
          const optionBtn = modal.querySelector(
            `.fsb-modal-variant-option[data-option-name="${option.name}"][data-option-value="${optionValue}"]`
          );
          if (optionBtn) {
            const optionGroup = optionBtn.closest('.fsb-modal-variant-selector__options');
            if (optionGroup) {
              optionGroup.querySelectorAll('.fsb-modal-variant-option').forEach(b => {
                b.classList.remove('fsb-modal-variant-option--selected');
              });
            }
            optionBtn.classList.add('fsb-modal-variant-option--selected');
            
            const valueDisplay = optionBtn.closest('.fsb-modal-variant-selector')?.querySelector(
              `[data-modal-option-value="${option.name}"]`
            );
            if (valueDisplay) {
              valueDisplay.textContent = optionValue;
            }
          }
        }
      });
      
      // Enable the select button
      if (selectBtn) selectBtn.disabled = false;
      
      // Update gallery for selected variant
      if (selectedVariant) {
        setTimeout(() => {
          this.updateModalGalleryForVariant(selectedVariant.id);
        }, 100);
        this.updateModalPriceForVariant(selectedVariant);
      }
    } else {
      this.preselectFirstAvailableVariant(modal, variants, productOptions);
    }

    // Show modal with animation
    modal.classList.add('fsb-whats-in-box__modal--open');
    document.body.style.overflow = 'hidden';

    // Initialize Swiper if not already done
    setTimeout(() => {
      this.initModalSwiper(modal);
    }, 50);
  }

  /**
   * Open add-on product modal (simpler version without variant selector)
   * @param {string} productId - The product ID
   * @param {string|null} variantId - Optional variant ID for split products
   */
  openAddonModal(productId, variantId = null) {
    const modal = this.container.querySelector(`[data-addon-modal="${productId}"]`);
    
    if (!modal) return;

    // Find the add-on card that triggered this modal
    let addonCard = null;
    if (variantId) {
      // For split products, find by variant ID
      addonCard = this.container.querySelector(`.fsb-add-ons__product-card [data-variant-id="${variantId}"]`)?.closest('.fsb-add-ons__product-card');
    }
    if (!addonCard) {
      // Fallback to product ID
      addonCard = this.container.querySelector(`.fsb-add-ons__product-card[data-product-id="${productId}"]`);
    }

    // Store references
    this.currentModal = modal;
    this.currentAddonCard = addonCard;
    this.currentAddonVariantId = variantId;
    
    // Check if this add-on is already selected
    const isAddonSelected = addonCard?.classList.contains('fsb-add-ons__product-card--selected') || false;
    
    // Update Add/Remove button visibility
    const addBtn = modal.querySelector('[data-addon-modal-add]');
    const removeBtn = modal.querySelector('[data-addon-modal-remove]');
    if (addBtn) addBtn.hidden = isAddonSelected;
    if (removeBtn) removeBtn.hidden = !isAddonSelected;
    
    // Copy price from add-on card into modal
    if (addonCard) {
      const cardPriceCurrent = addonCard.querySelector('.fsb-add-ons__product-low-container-price-current');
      const cardPriceCompare = addonCard.querySelector('.fsb-add-ons__product-low-container-price-compare');
      const modalPriceCurrent = modal.querySelector('[data-price-current]');
      const modalPriceCompare = modal.querySelector('[data-price-compare]');
      
      if (modalPriceCurrent && cardPriceCurrent) {
        modalPriceCurrent.textContent = cardPriceCurrent.textContent;
      }
      
      if (modalPriceCompare) {
        if (cardPriceCompare) {
          modalPriceCompare.textContent = cardPriceCompare.textContent;
          modalPriceCompare.hidden = false;
        } else {
          modalPriceCompare.hidden = true;
        }
      }
    }
    
    // Parse variants with custom galleries
    let variantsWithGallery = [];
    try {
      const galleryVariantsJson = modal.querySelector('[data-addon-variants-with-gallery]');
      if (galleryVariantsJson) {
        variantsWithGallery = JSON.parse(galleryVariantsJson.textContent);
      }
    } catch (e) {
      // Silently fail
    }
    
    // Determine which gallery to show
    let gallerySetToShow = 'default';
    
    if (variantId) {
      const variantIdNum = parseInt(variantId, 10);
      // Check if this variant has a custom gallery
      if (variantsWithGallery.includes(variantIdNum)) {
        gallerySetToShow = variantId;
      }
    }
    
    // Hide all gallery sets and show the correct one
    const gallerySets = modal.querySelectorAll('[data-gallery-set]');
    gallerySets.forEach(set => {
      if (set.dataset.gallerySet === gallerySetToShow) {
        set.hidden = false;
      } else {
        set.hidden = true;
      }
    });
    
    // Show the correct variant display if this is a split product with variant ID
    const variantDisplays = modal.querySelectorAll('[data-variant-display]');
    if (variantDisplays.length > 0 && variantId) {
      variantDisplays.forEach(display => {
        if (display.dataset.variantDisplay === variantId) {
          display.hidden = false;
        } else {
          display.hidden = true;
        }
      });
    }
    
    // Show modal with animation
    modal.classList.add('fsb-whats-in-box__modal--open');
    document.body.style.overflow = 'hidden';

    // Initialize Swiper for the visible gallery
    setTimeout(() => {
      const activeGallerySet = modal.querySelector(`[data-gallery-set="${gallerySetToShow}"]`);
      this.initModalSwiper(modal, activeGallerySet);
    }, 50);
  }

  /**
   * Handle Add button click in add-on modal
   */
  handleAddonModalAdd() {
    if (!this.currentAddonCard) return;
    
    // Find the add button on the card and trigger a click
    const addButton = this.currentAddonCard.querySelector('.fsb-add-ons__add-button');
    if (addButton) {
      addButton.click();
    }
    
    // Close the modal
    this.closeModal();
  }

  /**
   * Handle Remove button click in add-on modal
   */
  handleAddonModalRemove() {
    if (!this.currentAddonCard) return;
    
    // The add button toggles selection, so clicking it again will deselect
    const addButton = this.currentAddonCard.querySelector('.fsb-add-ons__add-button');
    if (addButton) {
      addButton.click();
    }
    
    // Close the modal
    this.closeModal();
  }

  /**
   * Open add-on modal with variant selectors (for non-split products)
   */
  openAddonModalWithVariants(productId, card) {
    const modal = this.container.querySelector(`[data-addon-modal="${productId}"]`);
    if (!modal) return;

    // Store references
    this.currentModal = modal;
    this.currentAddonCard = card;
    this.addonModalSelectedOptions = {};

    // Parse modal data from embedded JSON
    let variants = [];
    let productOptions = [];
    let variantImageMap = {};
    let variantsWithGallery = [];

    try {
      const variantsJson = modal.querySelector('[data-addon-modal-variants-json]');
      const optionsJson = modal.querySelector('[data-addon-modal-options-json]');
      const imageMapJson = modal.querySelector('[data-addon-variant-image-map]');
      const galleryVariantsJson = modal.querySelector('[data-addon-variants-with-gallery]');

      if (variantsJson) variants = JSON.parse(variantsJson.textContent);
      if (optionsJson) productOptions = JSON.parse(optionsJson.textContent);
      if (imageMapJson) variantImageMap = JSON.parse(imageMapJson.textContent);
      if (galleryVariantsJson) variantsWithGallery = JSON.parse(galleryVariantsJson.textContent);
    } catch (e) {
      // Silently handle parsing errors
    }

    // Store for later use
    this.addonModalVariants = variants;
    this.addonModalOptions = productOptions;
    this.addonModalVariantImageMap = variantImageMap;
    this.addonModalVariantsWithGallery = variantsWithGallery;

    // Check if this add-on has already been selected
    const isSelected = card?.classList.contains('fsb-add-ons__product-card--selected') || false;

    // Update button states
    const selectBtn = modal.querySelector('[data-addon-modal-select]');
    const removeBtn = modal.querySelector('[data-addon-modal-remove-option]');

    if (selectBtn) {
      const optionsCount = productOptions.length;
      if (isSelected) {
        selectBtn.textContent = optionsCount > 1 ? 'Change Options' : 'Change Option';
      } else {
        selectBtn.textContent = optionsCount > 1 ? 'Choose Options' : 'Choose Option';
      }
    }
    if (removeBtn) removeBtn.hidden = !isSelected;

    // Copy price from add-on card into modal
    if (card) {
      const cardPriceCurrent = card.querySelector('.fsb-add-ons__product-low-container-price-current');
      const cardPriceCompare = card.querySelector('.fsb-add-ons__product-low-container-price-compare');
      const modalPriceCurrent = modal.querySelector('[data-price-current]');
      const modalPriceCompare = modal.querySelector('[data-price-compare]');
      
      if (modalPriceCurrent && cardPriceCurrent) {
        modalPriceCurrent.textContent = cardPriceCurrent.textContent;
      }
      
      if (modalPriceCompare) {
        if (cardPriceCompare) {
          modalPriceCompare.textContent = cardPriceCompare.textContent;
          modalPriceCompare.style.display = '';
        } else {
          modalPriceCompare.style.display = 'none';
        }
      }
    }

    // Reset option selections
    const optionButtons = modal.querySelectorAll('.fsb-modal-variant-option');
    optionButtons.forEach(btn => btn.classList.remove('fsb-modal-variant-option--selected'));

    // Reset option value displays
    productOptions.forEach(option => {
      const valueDisplay = modal.querySelector(`[data-modal-option-value="${option.name}"]`);
      if (valueDisplay) {
        valueDisplay.textContent = `Select ${option.name}`;
      }
    });

    // If already selected, pre-select the current variant
    if (isSelected) {
      this.preSelectAddonModalVariant(modal, card, variants, productOptions);
    } else {
      // Pre-select first available variant
      this.preSelectFirstAddonVariant(modal, variants, productOptions);
    }

    // Show the default gallery
    const gallerySets = modal.querySelectorAll('[data-gallery-set]');
    gallerySets.forEach(set => {
      set.hidden = set.dataset.gallerySet !== 'default';
    });

    // Show modal
    modal.classList.add('fsb-whats-in-box__modal--open');
    document.body.style.overflow = 'hidden';

    // Initialize Swiper
    setTimeout(() => {
      const activeGallerySet = modal.querySelector('[data-gallery-set="default"]');
      this.initModalSwiper(modal, activeGallerySet);
    }, 50);
  }

  /**
   * Pre-select the variant that's already selected on the card
   */
  preSelectAddonModalVariant(modal, card, variants, productOptions) {
    // Get the selected variant ID from the card
    const selectedVariantId = card.dataset.selectedVariantId;
    if (!selectedVariantId) return;

    const selectedVariant = variants.find(v => String(v.id) === String(selectedVariantId));
    if (!selectedVariant) return;

    // Select each option based on the selected variant
    productOptions.forEach((option, idx) => {
      const optionValue = selectedVariant[`option${idx + 1}`];
      if (optionValue) {
        this.addonModalSelectedOptions[option.name] = optionValue;

        // Update UI
        const optionBtn = modal.querySelector(`.fsb-modal-variant-option[data-option-name="${option.name}"][data-option-value="${optionValue}"]`);
        if (optionBtn) {
          optionBtn.classList.add('fsb-modal-variant-option--selected');
        }

        const valueDisplay = modal.querySelector(`[data-modal-option-value="${option.name}"]`);
        if (valueDisplay) {
          valueDisplay.textContent = optionValue;
        }
      }
    });

    // Enable select button
    const selectBtn = modal.querySelector('[data-addon-modal-select]');
    if (selectBtn) selectBtn.disabled = false;
  }

  /**
   * Pre-select first available variant in add-on modal
   */
  preSelectFirstAddonVariant(modal, variants, productOptions) {
    // If only one variant, pre-select it
    if (variants.length === 1) {
      productOptions.forEach((opt, idx) => {
        this.addonModalSelectedOptions[opt.name] = variants[0][`option${idx + 1}`];
        
        // Update UI
        const optionBtn = modal.querySelector(`.fsb-modal-variant-option[data-option-name="${opt.name}"][data-option-value="${variants[0][`option${idx + 1}`]}"]`);
        if (optionBtn) {
          optionBtn.classList.add('fsb-modal-variant-option--selected');
        }
        
        const valueDisplay = modal.querySelector(`[data-modal-option-value="${opt.name}"]`);
        if (valueDisplay) {
          valueDisplay.textContent = variants[0][`option${idx + 1}`];
        }
      });
      const selectBtn = modal.querySelector('[data-addon-modal-select]');
      if (selectBtn) selectBtn.disabled = false;
      return;
    }

    // Find the first available variant
    const firstAvailableVariant = variants.find(v => v.available);
    if (!firstAvailableVariant) {
      const selectBtn = modal.querySelector('[data-addon-modal-select]');
      if (selectBtn) selectBtn.disabled = true;
      return;
    }

    // Pre-select each option
    productOptions.forEach((option, idx) => {
      const optionValue = firstAvailableVariant[`option${idx + 1}`];
      if (optionValue) {
        this.addonModalSelectedOptions[option.name] = optionValue;

        // Update UI
        const optionBtn = modal.querySelector(`.fsb-modal-variant-option[data-option-name="${option.name}"][data-option-value="${optionValue}"]`);
        if (optionBtn) {
          optionBtn.classList.add('fsb-modal-variant-option--selected');
        }

        const valueDisplay = modal.querySelector(`[data-modal-option-value="${option.name}"]`);
        if (valueDisplay) {
          valueDisplay.textContent = optionValue;
        }
      }
    });

    // Enable select button
    const selectBtn = modal.querySelector('[data-addon-modal-select]');
    if (selectBtn) selectBtn.disabled = false;
  }

  /**
   * Handle variant option click in add-on modal
   */
  handleAddonModalOptionClick(e, modal) {
    const btn = e.target.closest('.fsb-modal-variant-option');
    if (!btn || btn.disabled) return;

    const optionName = btn.dataset.optionName;
    const optionValue = btn.dataset.optionValue;

    // Update selection
    this.addonModalSelectedOptions[optionName] = optionValue;

    // Update UI - remove selected from siblings
    const siblings = modal.querySelectorAll(`.fsb-modal-variant-option[data-option-name="${optionName}"]`);
    siblings.forEach(sib => sib.classList.remove('fsb-modal-variant-option--selected'));
    btn.classList.add('fsb-modal-variant-option--selected');

    // Update header value display
    const valueDisplay = modal.querySelector(`[data-modal-option-value="${optionName}"]`);
    if (valueDisplay) {
      valueDisplay.textContent = optionValue;
    }

    // Check if all options are selected
    const allSelected = this.addonModalOptions.every(opt => this.addonModalSelectedOptions[opt.name]);
    const selectBtn = modal.querySelector('[data-addon-modal-select]');

    if (allSelected) {
      // Find exact matching variant
      const exactVariant = this.addonModalVariants.find(v => {
        return this.addonModalOptions.every((opt, idx) => {
          return v[`option${idx + 1}`] === this.addonModalSelectedOptions[opt.name];
        });
      });

      if (exactVariant && exactVariant.available) {
        if (selectBtn) selectBtn.disabled = false;
        
        // Update gallery if needed
        this.updateAddonModalGallery(modal, exactVariant.id);
        
        // Navigate to variant image
        if (this.addonModalVariantImageMap[String(exactVariant.id)] !== undefined) {
          this.navigateToVariantImageInAddonModal(modal, exactVariant.id);
        }
      } else {
        if (selectBtn) selectBtn.disabled = true;
      }
    } else {
      if (selectBtn) selectBtn.disabled = true;
    }
  }

  /**
   * Update add-on modal gallery for variant
   */
  updateAddonModalGallery(modal, variantId) {
    const hasCustomGallery = this.addonModalVariantsWithGallery.includes(variantId);
    const gallerySetToShow = hasCustomGallery ? String(variantId) : 'default';

    const gallerySets = modal.querySelectorAll('[data-gallery-set]');
    gallerySets.forEach(set => {
      if (set.dataset.gallerySet === gallerySetToShow) {
        set.hidden = false;
      } else {
        set.hidden = true;
      }
    });

    // Re-init swiper if gallery changed
    if (hasCustomGallery) {
      const activeGallerySet = modal.querySelector(`[data-gallery-set="${gallerySetToShow}"]`);
      this.initModalSwiper(modal, activeGallerySet);
    }
  }

  /**
   * Navigate to variant image in add-on modal
   */
  navigateToVariantImageInAddonModal(modal, variantId) {
    const imageIndex = this.addonModalVariantImageMap[String(variantId)];
    if (imageIndex === undefined) return;

    const swiperEl = modal.querySelector('.fsb-whats-in-box__modal-swiper');
    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.slideTo(imageIndex);
    }
  }

  /**
   * Update add-on card image to show the selected variant's image
   */
  updateAddonCardImage(card, variantId) {
    const imageContainer = card.querySelector('.fsb-add-ons__product-image-container');
    if (!imageContainer) return;

    const allImages = imageContainer.querySelectorAll('.fsb-add-ons__variant-image');
    if (allImages.length === 0) return;

    // Hide all images first
    allImages.forEach(img => {
      img.classList.remove('fsb-add-ons__variant-image--active');
    });

    // Find and show the variant's image
    let targetImage = imageContainer.querySelector(`[data-variant-image="${variantId}"]`);
    
    // If no variant-specific image found, show the default image
    if (!targetImage) {
      targetImage = imageContainer.querySelector('[data-variant-image="default"]');
    }

    if (targetImage) {
      targetImage.classList.add('fsb-add-ons__variant-image--active');
    }
  }

  /**
   * Reset add-on card image to default
   */
  resetAddonCardImage(card) {
    const imageContainer = card.querySelector('.fsb-add-ons__product-image-container');
    if (!imageContainer) return;

    const allImages = imageContainer.querySelectorAll('.fsb-add-ons__variant-image');
    
    // Hide all images first
    allImages.forEach(img => {
      img.classList.remove('fsb-add-ons__variant-image--active');
    });

    // Show the default image
    const defaultImage = imageContainer.querySelector('[data-variant-image="default"]');
    if (defaultImage) {
      defaultImage.classList.add('fsb-add-ons__variant-image--active');
    }
  }

  /**
   * Handle "Choose Option" / "Change Option" click in add-on modal
   */
  handleAddonModalSelectOption() {
    if (!this.currentModal || !this.currentAddonCard) return;

    // Find the selected variant
    const selectedVariant = this.addonModalVariants.find(v => {
      return this.addonModalOptions.every((opt, idx) => {
        return v[`option${idx + 1}`] === this.addonModalSelectedOptions[opt.name];
      });
    });

    if (!selectedVariant) return;

    const card = this.currentAddonCard;
    
    // Get previously selected variant ID BEFORE updating
    const previousVariantId = card.dataset.selectedVariantId;

    // Update card state
    card.classList.add('fsb-add-ons__product-card--selected');

    // Update card's variant selection display
    this.addonModalOptions.forEach(option => {
      const optionDisplay = card.querySelector(`.fsb-add-ons__selection-option[data-option-name="${option.name}"] .fsb-add-ons__selection-option-value`);
      if (optionDisplay) {
        optionDisplay.textContent = this.addonModalSelectedOptions[option.name];
      }
    });

    // Update card's button text
    const cardBtn = card.querySelector('.fsb-add-ons__select-option-button');
    if (cardBtn) {
      const optionsCount = this.addonModalOptions.length;
      cardBtn.textContent = optionsCount > 1 ? 'Change Options' : 'Change Option';
      cardBtn.dataset.hasSelection = 'true';
    }

    // Update card's image to show selected variant image
    this.updateAddonCardImage(card, selectedVariant.id);

    // Remove previously selected variant from selectedAddOns (if any) before adding new one
    if (previousVariantId && previousVariantId !== String(selectedVariant.id)) {
      this.selectedAddOns.delete(parseInt(previousVariantId, 10));
    }
    
    // Update card's selected variant ID and add new variant to selected add-ons
    card.dataset.selectedVariantId = selectedVariant.id;
    this.selectedAddOns.add(selectedVariant.id);

    // Trigger bundle update
    this.updateTotalBundlePrice();
    this.updateAddToCartButtonState();
    this.updateLineItemProperties();

    // Close modal
    this.closeModal();
  }

  /**
   * Handle "Remove Option" click in add-on modal (for non-split products)
   */
  handleAddonModalRemoveOption() {
    if (!this.currentModal || !this.currentAddonCard) return;

    const card = this.currentAddonCard;
    const selectedVariantId = parseInt(card.dataset.selectedVariantId, 10);

    // Remove from selected add-ons
    if (selectedVariantId) {
      this.selectedAddOns.delete(selectedVariantId);
    }

    // Reset card state
    card.classList.remove('fsb-add-ons__product-card--selected');
    delete card.dataset.selectedVariantId;

    // Reset variant selection display
    this.addonModalOptions.forEach(option => {
      const optionDisplay = card.querySelector(`.fsb-add-ons__selection-option[data-option-name="${option.name}"] .fsb-add-ons__selection-option-value`);
      if (optionDisplay) {
        optionDisplay.textContent = `Select ${option.name}`;
      }
    });

    // Reset button text
    const cardBtn = card.querySelector('.fsb-add-ons__select-option-button');
    if (cardBtn) {
      cardBtn.textContent = 'See Options';
      cardBtn.dataset.hasSelection = 'false';
    }

    // Reset image to default
    this.resetAddonCardImage(card);

    // Trigger bundle update
    this.updateTotalBundlePrice();
    this.updateAddToCartButtonState();
    this.updateLineItemProperties();

    // Close modal
    this.closeModal();
  }

  /**
   * Deselect an add-on via checkmark click
   */
  deselectAddOn(card) {
    if (!card) return;

    // Check if this is a split product (has add button) or non-split (has select option button)
    const addButton = card.querySelector('.fsb-add-ons__add-button');
    const selectOptionButton = card.querySelector('.fsb-add-ons__select-option-button');

    if (addButton) {
      // Split product - simulate clicking the add button to toggle off
      addButton.click();
    } else if (selectOptionButton) {
      // Non-split product - need to deselect manually
      const selectedVariantId = parseInt(card.dataset.selectedVariantId, 10);

      // Remove from selected add-ons
      if (selectedVariantId) {
        this.selectedAddOns.delete(selectedVariantId);
      }

      // Reset card state
      card.classList.remove('fsb-add-ons__product-card--selected');
      delete card.dataset.selectedVariantId;

      // Reset variant selection display
      const selectionOptions = card.querySelectorAll('.fsb-add-ons__selection-option');
      selectionOptions.forEach(optionEl => {
        const optionName = optionEl.dataset.optionName;
        const valueDisplay = optionEl.querySelector('.fsb-add-ons__selection-option-value');
        if (valueDisplay && optionName) {
          valueDisplay.textContent = `Select ${optionName}`;
        }
      });

      // Reset button text
      selectOptionButton.textContent = 'See Options';
      selectOptionButton.dataset.hasSelection = 'false';

      // Reset image to default
      this.resetAddonCardImage(card);

      // Trigger bundle update
      this.updateTotalBundlePrice();
      this.updateAddToCartButtonState();
      this.updateLineItemProperties();
    }
  }

  /**
   * Navigate the modal swiper to a specific variant's image
   */
  navigateToVariantImage(variantId) {
    if (!this.currentModal || !this.modalVariantImageMap) return;

    const variantIdStr = String(variantId);
    const imageIndex = this.modalVariantImageMap[variantIdStr];

    if (imageIndex !== undefined && imageIndex !== null) {
      // Find the active swiper in the current modal
      const activeGallerySet = this.currentModal.querySelector('[data-gallery-set]:not([hidden])');
      const mainSwiperEl = activeGallerySet?.querySelector('.fsb-whats-in-box__modal-swiper');
      
      if (mainSwiperEl?.swiper) {
        mainSwiperEl.swiper.slideTo(imageIndex);
      }
    }
  }

  /**
   * Update the modal gallery for a specific variant
   * Show/hide pre-rendered gallery sets based on variant ID
   */
  updateModalGalleryForVariant(variantId) {
    if (!this.currentModal) return;

    const variantIdStr = String(variantId);
    const hasCustomGallery = this.variantsWithGallery && this.variantsWithGallery.includes(variantId);
    
    // Hide all gallery sets first
    const allGallerySets = this.currentModal.querySelectorAll('[data-gallery-set]');
    allGallerySets.forEach(set => {
      set.hidden = true;
    });
    
    if (hasCustomGallery) {
      // Show variant-specific gallery
      const variantGallery = this.currentModal.querySelector(`[data-gallery-set="${variantIdStr}"]`);
      if (variantGallery) {
        variantGallery.hidden = false;
        this.initModalSwiper(this.currentModal, variantGallery);
      }
    } else {
      // Show default gallery and navigate to variant's image
      const defaultGallery = this.currentModal.querySelector('[data-gallery-set="default"]');
      if (defaultGallery) {
        defaultGallery.hidden = false;
        this.initModalSwiper(this.currentModal, defaultGallery);
        
        // Navigate to variant's featured image
        setTimeout(() => {
          this.navigateToVariantImage(variantId);
        }, 100);
      }
    }
  }

  /**
   * Initialize Swiper for a specific gallery set in a modal
   */
  initModalSwiper(modal, gallerySet = null) {
    if (typeof Swiper === 'undefined') return;
    
    // Use provided gallery set or find the visible one
    const activeGallerySet = gallerySet || modal.querySelector('[data-gallery-set]:not([hidden])');
    if (!activeGallerySet) return;
    
    const mainSwiperEl = activeGallerySet.querySelector('.fsb-whats-in-box__modal-swiper');
    const thumbsSwiperEl = activeGallerySet.querySelector('.fsb-whats-in-box__modal-thumbs');
    
    if (!mainSwiperEl) return;
    
    // Check if Swiper already initialized on these elements
    if (mainSwiperEl.swiper) {
      mainSwiperEl.swiper.update();
      if (thumbsSwiperEl?.swiper) thumbsSwiperEl.swiper.update();
      return;
    }
    
    const slideCount = mainSwiperEl.querySelectorAll('.swiper-slide').length;
    
    // Initialize thumbnails swiper first (if more than 1 image)
    let thumbsSwiper = null;
    if (slideCount > 1 && thumbsSwiperEl) {
      thumbsSwiper = new Swiper(thumbsSwiperEl, {
        spaceBetween: 10,
        slidesPerView: 4,
        freeMode: true,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        breakpoints: {
          768: {
            slidesPerView: 5
          }
        }
      });
    }

    // Initialize main swiper
    new Swiper(mainSwiperEl, {
      spaceBetween: 10,
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined
    });
  }

  closeModal() {
    if (!this.currentModal) return;

    // Hide modal with animation
    this.currentModal.classList.remove('fsb-whats-in-box__modal--open');
    document.body.style.overflow = '';

    // Reset state
    this.currentModal = null;
    this.currentModalCard = null;
    this.modalSelectedOptions = {};
  }

  /**
   * Update the modal price display based on the product card's variant data
   * @param {HTMLElement} card - Product card element
   * @param {HTMLElement} modal - Modal element
   */
  updateModalPrice(card, modal) {
    const modalPrice = modal.querySelector('[data-modal-price]');
    if (!modalPrice) return;
    
    const currentEl = modalPrice.querySelector('[data-price-current]');
    const compareEl = modalPrice.querySelector('[data-price-compare]');
    
    if (!currentEl) return;
    
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    
    // Get the selected variant for this specific product
    const categoryTitle = card.dataset.categoryTitle;
    const productId = card.dataset.productId;
    const variantKey = `${categoryTitle}-${productId}`;
    let variant = this.selectedVariants.get(variantKey);
    
    if (!variant && variants.length > 0) {
      variant = variants.find(v => v.available && (v.id in appVariants || `gid://shopify/ProductVariant/${v.id}` in appVariants)) ||
                variants.find(v => v.available) ||
                variants[0];
    }
    
    if (!variant) {
      currentEl.textContent = '';
      if (compareEl) compareEl.textContent = '';
      return;
    }
    
    // Calculate prices
    const comparePrice = variant.compare_at_price || variant.price || 0;
    const basePrice = variant.price || 0;
    
    // Calculate bundle price using discount percentage if dynamic pricing is enabled
    let bundlePriceCents = basePrice;
    if (this.isDynamicPricing && this.discountPercentage > 0) {
      bundlePriceCents = basePrice * (1 - this.discountPercentage / 100);
    } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
      const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
      bundlePriceCents = parseFloat(appVariants[appVariantKey]) * 100;
    }
    
    // Display current price
    currentEl.textContent = this.formatPrice(bundlePriceCents);
    
    // Display compare price (strikethrough) if bundle price is less than compare price
    if (compareEl && comparePrice && bundlePriceCents < comparePrice) {
      compareEl.textContent = this.formatPrice(comparePrice);
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.textContent = '';
      compareEl.style.display = 'none';
    }
  }

  /**
   * Update the modal price display for a specific variant
   * @param {Object} variant - The variant object
   */
  updateModalPriceForVariant(variant) {
    if (!this.currentModal || !variant) return;
    
    const modalPrice = this.currentModal.querySelector('[data-modal-price]');
    if (!modalPrice) return;
    
    const currentEl = modalPrice.querySelector('[data-price-current]');
    const compareEl = modalPrice.querySelector('[data-price-compare]');
    
    if (!currentEl) return;
    
    // Get appVariants from the current modal card
    const appVariants = this.currentModalCard ? JSON.parse(this.currentModalCard.dataset.appVariants || '{}') : {};
    
    // Calculate prices
    const comparePrice = variant.compare_at_price || variant.price || 0;
    const basePrice = variant.price || 0;
    
    // Calculate bundle price using discount percentage if dynamic pricing is enabled
    let bundlePriceCents = basePrice;
    if (this.isDynamicPricing && this.discountPercentage > 0) {
      bundlePriceCents = basePrice * (1 - this.discountPercentage / 100);
    } else if (appVariants && (variant.id in appVariants || `gid://shopify/ProductVariant/${variant.id}` in appVariants)) {
      const appVariantKey = variant.id in appVariants ? variant.id : `gid://shopify/ProductVariant/${variant.id}`;
      bundlePriceCents = parseFloat(appVariants[appVariantKey]) * 100;
    }
    
    // Display current price
    currentEl.textContent = this.formatPrice(bundlePriceCents);
    
    // Display compare price (strikethrough) if bundle price is less than compare price
    if (compareEl && comparePrice && bundlePriceCents < comparePrice) {
      compareEl.textContent = this.formatPrice(comparePrice);
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.textContent = '';
      compareEl.style.display = 'none';
    }
  }

  /**
   * Pre-select the first available variant in the pre-rendered modal
   */
  preselectFirstAvailableVariant(modal, variants, productOptions) {
    // If only one variant, auto-select it
    if (variants.length === 1) {
      this.modalSelectedOptions = {};
      productOptions.forEach((opt, idx) => {
        this.modalSelectedOptions[opt.name] = variants[0][`option${idx + 1}`];
      });
      const selectBtn = modal.querySelector('[data-modal-select]');
      if (selectBtn) selectBtn.disabled = false;
      return;
    }
    
    // Find the first available variant
    const firstAvailableVariant = variants.find(v => v.available);
    
    if (!firstAvailableVariant) {
      const selectBtn = modal.querySelector('[data-modal-select]');
      if (selectBtn) selectBtn.disabled = true;
      return;
    }

    // Pre-select each option based on the first available variant
    productOptions.forEach((option, idx) => {
      const optionValue = firstAvailableVariant[`option${idx + 1}`];
      if (optionValue) {
        this.modalSelectedOptions[option.name] = optionValue;

        // Find and update the corresponding button in the modal
        const optionBtn = modal.querySelector(
          `.fsb-modal-variant-option[data-option-name="${option.name}"][data-option-value="${optionValue}"]`
        );
        
        if (optionBtn) {
          // Update selected class
          const optionGroup = optionBtn.closest('.fsb-modal-variant-selector__options');
          if (optionGroup) {
            optionGroup.querySelectorAll('.fsb-modal-variant-option').forEach(b => {
              b.classList.remove('fsb-modal-variant-option--selected');
            });
          }
          optionBtn.classList.add('fsb-modal-variant-option--selected');

          // Update displayed value
          const valueDisplay = optionBtn.closest('.fsb-modal-variant-selector')?.querySelector(
            `[data-modal-option-value="${option.name}"]`
          );
          if (valueDisplay) {
            valueDisplay.textContent = optionValue;
          }
        }
      }
    });

    // Update gallery for this variant (after swiper is initialized)
    setTimeout(() => {
      this.updateModalGalleryForVariant(firstAvailableVariant.id);
    }, 100);
    
    // Update modal price for this variant
    this.updateModalPriceForVariant(firstAvailableVariant);

    // Enable select button since we have a valid selection
    const selectBtn = modal.querySelector('[data-modal-select]');
    if (selectBtn) selectBtn.disabled = false;
  }

  /**
   * Handle variant option click in modal
   */
  handleModalOptionClick(e) {
    const btn = e.target.closest('.fsb-modal-variant-option');
    if (!btn || !this.currentModal) return;

    const optionName = btn.dataset.optionName;
    const optionValue = btn.dataset.optionValue;
    const variants = this.modalVariants_data;
    const productOptions = this.modalOptions_data;

    // Update selected options
    this.modalSelectedOptions[optionName] = optionValue;

    // Update UI - toggle selected class
    const optionGroup = btn.closest('.fsb-modal-variant-selector__options');
    if (optionGroup) {
      optionGroup.querySelectorAll('.fsb-modal-variant-option').forEach(b => {
        b.classList.remove('fsb-modal-variant-option--selected');
      });
    }
    btn.classList.add('fsb-modal-variant-option--selected');

    // Update displayed value
    const valueDisplay = btn.closest('.fsb-modal-variant-selector')?.querySelector(`[data-modal-option-value="${optionName}"]`);
    if (valueDisplay) {
      valueDisplay.textContent = optionValue;
    }

    // Try to find a matching variant based on current selections
    const selectedVariant = variants.find(v => {
      return productOptions.every((opt, idx) => {
        const selectedValue = this.modalSelectedOptions[opt.name];
        if (selectedValue) {
          return v[`option${idx + 1}`] === selectedValue;
        }
        return true;
      });
    });

    // Update gallery and price for selected variant
    if (selectedVariant) {
      this.updateModalGalleryForVariant(selectedVariant.id);
      this.updateModalPriceForVariant(selectedVariant);
    }

    // Check if all options are selected
    const allSelected = productOptions.every(opt => this.modalSelectedOptions[opt.name]);
    const selectBtn = this.currentModal.querySelector('[data-modal-select]');
    
    if (allSelected) {
      // Find exact matching variant
      const exactVariant = variants.find(v => {
        return productOptions.every((opt, idx) => {
          return v[`option${idx + 1}`] === this.modalSelectedOptions[opt.name];
        });
      });

      if (selectBtn) {
        selectBtn.disabled = !(exactVariant && exactVariant.available);
      }
    } else if (selectBtn) {
      selectBtn.disabled = true;
    }
  }

  handleModalSelectOption() {
    if (!this.currentModalCard || Object.keys(this.modalSelectedOptions).length === 0) return;

    const card = this.currentModalCard;
    const categoryTitle = card.dataset.categoryTitle;
    const isAddOn = categoryTitle === 'add-ons';
    const variantKey = `${categoryTitle}-${card.dataset.productId}`;
    
    // Parse card data
    const variants = JSON.parse(card.dataset.variants || '[]');
    const appVariants = JSON.parse(card.dataset.appVariants || '{}');
    const productOptions = JSON.parse(card.dataset.productOptions || '[]');
    
    // Find the variant that matches all selected options
    const selectedVariant = variants.find(v => {
      return productOptions.every((opt, idx) => {
        return v[`option${idx + 1}`] === this.modalSelectedOptions[opt.name];
      });
    });
    
    if (!selectedVariant || !selectedVariant.available) {
      return;
    }
    
    // Update selected options array
    const optionsArray = productOptions.map(opt => this.modalSelectedOptions[opt.name] || '');
    this.selectedOptions.set(variantKey, optionsArray);
    
    // Update selected variant
    this.selectedVariants.set(variantKey, selectedVariant);
    
    // Update product image to show selected variant's image
    this.updateProductImage(card, selectedVariant);
    
    // Update selection option display values
    productOptions.forEach((opt, idx) => {
      const value = this.modalSelectedOptions[opt.name];
      if (value) {
        this.updateSelectionOptionValue(card, idx, value);
      }
    });
    
    // Update product price
    if (this.blockSettings.show_price || this.isDynamicPricing) {
      this.updateProductPrice(card, selectedVariant, appVariants);
    }
    
    // Mark card as selected
    const selectedClass = isAddOn 
      ? 'fsb-add-ons__product-card--selected'
      : 'fsb-whats-in-box__product-card--selected';
    card.classList.add(selectedClass);
    
    // Update "Select Option" button to "Change Option(s)" or "Learn More" for required-in-bundle
    const selectOptionBtn = card.querySelector('[data-see-details-trigger]');
    if (selectOptionBtn) {
      if (card.dataset.requiredInBundle === 'true') {
        selectOptionBtn.textContent = 'Learn More';
      } else {
        let optionsCount;
        try {
          const productOptions = JSON.parse(card.dataset.productOptions || '[]');
          optionsCount = productOptions.length;
        } catch (e) {
          optionsCount = 1;
        }
        selectOptionBtn.textContent = optionsCount > 1 ? 'Change Options' : 'Change Option';
      }
      selectOptionBtn.dataset.hasSelection = 'true';
    }
    
    // Update line item properties
    this.updateLineItemProperties();
    
    // Update price displays
    this.updateTotalBundlePrice();
    this.updateAddToCartButtonWithPrice();
    
    // Update button state (controls enabled/disabled and warning class)
    this.updateAddToCartButtonState();
    
    // Close the modal
    this.closeModal();
  }

  handleModalRemoveProduct() {
    if (!this.currentModalCard) return;

    // Use the same deselect logic as the checkmark
    this.deselectProduct(this.currentModalCard);
    
    // Close the modal
    this.closeModal();
  }
}

// Initialize the component
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.fsb-whats-in-box');
  containers.forEach(container => container._whatsInBox = new WhatsInBox(container));
  
  // Initialize keyboard navigation for scrollable add-on containers
  const scrollableContainers = document.querySelectorAll('.fsb-add-ons__product-card-container');
  scrollableContainers.forEach(container => {
    container.addEventListener('keydown', (e) => {
      if (document.activeElement !== container) {
        return;
      }
      
      const scrollAmount = 200;
      let scrollDelta = 0;
      
      if (e.key === 'ArrowLeft') {
        scrollDelta = -scrollAmount;
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        scrollDelta = scrollAmount;
        e.preventDefault();
      }
      
      if (scrollDelta !== 0) {
        container.scrollBy({
          left: scrollDelta,
          behavior: 'smooth'
        });
      }
    });
  });
});
